/**
 * 场景标注 · 编辑面板 · /scene-annotate
 *
 * 两种入参 (二选一):
 *   - ?codes=DC-4145326,LPL0000239532,PNC-100234
 *       批量新建标注入口,由 SceneGeneration 页 SceneAnnotateCard 跳来。
 *       ⚠ 一期后端 annotate-submit 接口未落地,批量入口暂不对外开放,
 *         页面命中 mode='code' 时直接展示"暂未开放"提示。
 *   - ?sceneId=126999
 *       单场景编辑入口,由 场景列表 / JobTask / SceneManagement 等页跳来。
 *       进入后 getSceneInspectionList({ ids:[sceneId] }) 拉元数据,
 *       把已有场景视为一条虚拟 "preview item"(resolved=true, source='existing'),队列长度=1。
 *
 * 单场景编辑通过 content 接口提交。本次只允许修改场景信息或文件的一侧；
 * 信息中的起止时间改变时重新生成，文件修改由后端同步共有字段。
 * 草稿在内存保留；生成任务和版本变化会拦截旧草稿，必须重新加载。
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useBlocker, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, App, AutoComplete, Button, Card, ConfigProvider, Empty, Form, Image, Input, InputNumber, Modal, Pagination, Popconfirm, Select, Space, Spin, Switch, Table, Tabs,
  TreeSelect, Tag, Tooltip, Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowLeftOutlined, BranchesOutlined, BuildOutlined, DeleteOutlined, EditOutlined, LeftOutlined, RightOutlined,
  CheckCircleOutlined, SaveOutlined, ThunderboltOutlined, ExclamationCircleOutlined,
  FileTextOutlined, HistoryOutlined, InfoCircleOutlined, ReloadOutlined,
} from '@ant-design/icons';
import {
  previewScenes, getSceneInspectionList,
  getSceneBosConfigFiles, getSceneBosConfigFileContent,
  getSceneSetsOfScene,
  getSceneContentBaseline, saveSceneContent,
  saveAsScene,
  deleteDerivedScenes,
  getSceneLabels,
  getSceneTagsOfScene,
  getSceneTagTree,
  batchAddScenesToTag,
  batchDeleteScenesFromTag,
  getSceneVersions,
  rollbackSceneToVersion,
  waiveScene,
  batchGenerateSceneByIds,
  type SceneVersion,
  type SceneHealth,
  type SceneTimeRerun,
  type ScenePreviewItem, type ScenePreviewFields, type SceneListItem,
  type SceneBosConfigFileItem, type SceneBosConfigFileContent,
  type SceneSetOfScene,
  type SceneContentBaseline,
  type SceneContentRequest,
  type SceneFieldPatch,
  type SceneEvent,
  type SceneTagNode, tagDisplayName,
  type EventTrigger,
  CLOUD_CMD_POLICY_HINT,
  CLOUD_CMD_POLICY_OPTIONS,
  isCloudCmdPolicy,
  type CloudCmdPolicy,
} from '@/apis/simulation';
import { ApiBusinessError } from '@/common/http/axios';
import SceneFileEditor from '@/components/SceneFileEditor';
import { useSceneContentDraft } from './useSceneContentDraft';
import { fieldPatch, objectDiffRows, pickEngineReason, requiresDraftReload, timeRangeChanged, versionDiffView, type VersionDiffView } from './sceneContentCodec';
import WebpPlayer from '@/components/WebpPlayer';
import { EventListEditor } from '@/components/EventListEditor';
import { SaveAsModal } from '@/components/SaveAsModal';

const { Text, Title } = Typography;
const ACCENT = '#5e6ad2';
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace';

/** 更新记录 diff 字段中文映射。
 *
 *  口径(2026-10-19):**字段名跟「场景信息」面板一字不差** —— 用户在面板上看到
 *  「故障时刻」,更新记录里就必须是「故障时刻」,不能是 ``jira`` / ``issue_time``
 *  这种库里的键名。key 是**叶子键**:``external.jira.issue_time`` 这种嵌套结构由
 *  `objectDiffRows` 展开到叶子后按 ``issue_time`` 查表。
 *  面板上没有的字段(引擎侧 ``scen_mode`` 等)保留一个说人话的名字即可。 */
const CHANGE_LOG_FIELD_LABELS: Record<string, string> = {
  scene_start_time: '场景开始时间',
  scene_end_time: '场景结束时间',
  map_name: '地图名称',
  map_version: '地图版本',
  issue_time: '故障时刻',
  label: '场景标签',
  description: '场景描述',
  open_switch_closed_loop_sec: '开环切闭环秒数',
  mask_finish_signal: '是否屏蔽 openAPI FINISH 信号',
  cloud_cmd_policy: '云控指令合并策略',
  scen_mode: '时间模式',
  params: '场景参数',
  external: '扩展信息',
  events: '云端控制事件',
};

/** 时间重跑链路的来源标签与状态文案（R1）。 */
const TIME_RERUN_STATUS_LABEL: Record<string, string> = {
  running: '重新生成中',
  resolved: '已重新生成',
  superseded: '已被后续构建取代',
};

const TIME_RERUN_STATUS_COLOR: Record<string, string> = {
  running: 'processing',
  resolved: 'green',
  superseded: 'default',
};

const formatSize = (n: number) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const displayValue = (v: unknown) => {
  if (v === null || v === undefined || v === '') return '空';
  if (typeof v === 'object') return '（结构化变更）';
  return String(v);
};

/** 结构化值（对象 / 数组）的完整内容:挂在 Tooltip 里,避免 diff 行直接渲染出
 *  "[object Object]"。截断展示,够看清改了什么又不撑爆 tooltip。 */
const structuredValueText = (v: unknown): string | null => {
  if (v === null || v === undefined || typeof v !== 'object') return null;
  try {
    const text = JSON.stringify(v, null, 2);
    return text.length > 2000 ? `${text.slice(0, 2000)}\n…（已截断）` : text;
  } catch {
    return null;
  }
};

/** 一条「工序轨道」的段落:原值 →(用户调整)→(引擎调整)。每段的 caption 就是
 *  「这一步是谁动的」,值跟着 caption 走 —— 一眼看出改了哪几步。 */
const buildDiffStages = (view: VersionDiffView) => {
  const stages: { caption: string; value: unknown; tone: 'origin' | 'user' | 'final' }[] = [
    { caption: '原值', value: view.before, tone: 'origin' },
  ];
  if (view.engineAdjusted && view.userChanged) {
    stages.push({ caption: '用户调整', value: view.requested, tone: 'user' });
  }
  // 引擎没再动过的字段只有两段:用户改的值就是最终值,不重复渲染第三段。
  stages.push({
    caption: view.finalActor === 'engine' ? '引擎调整'
      : view.finalActor === 'user' ? '用户调整' : '新值',
    value: view.after,
    tone: 'final',
  });
  return stages;
};

const DIFF_VALUE_STYLE: Record<string, CSSProperties> = {
  origin: { color: '#bfbfbf', textDecoration: 'line-through' },
  user: { color: '#595959' },
  final: { color: ACCENT, fontWeight: 600 },
};

/** 渲染一条轨道:各段竖排「caption + 值」,段间用 → 连。 */
const renderDiffTrack = (view: VersionDiffView) => {
  const captionStyle: Record<string, CSSProperties> = {
    origin: { color: '#bfbfbf' },
    user: { color: '#8c8c8c' },
    final: { color: view.finalActor === 'engine' ? ACCENT : '#8c8c8c' },
  };
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
      {buildDiffStages(view).map((stage, i) => {
        const json = structuredValueText(stage.value);
        return (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'flex-end', gap: 8 }}>
            {i > 0 && <span style={{ color: '#bfbfbf', lineHeight: '15px' }}>→</span>}
            <Tooltip title={json ? <pre style={{ margin: 0, fontSize: 11 }}>{json}</pre> : ''}>
              <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 1 }}>
                <span style={{ fontSize: 11, lineHeight: '13px', ...captionStyle[stage.tone] }}>
                  {stage.caption}
                </span>
                <span style={{
                  fontFamily: MONO, lineHeight: '16px', ...DIFF_VALUE_STYLE[stage.tone],
                }}>{displayValue(stage.value)}</span>
              </span>
            </Tooltip>
          </span>
        );
      })}
    </div>
  );
};

/** 时间格式化:后端来的 ISO 字符串(如 2026-08-04T20:22:16.000451 或 2026-08-04T20:22:16)
 *  统一转为 `YYYY-MM-DD HH:MM:SS.fff` 展示 (无 T,保留 3 位毫秒;缺失补 0)。
 *  非字符串或解析不出来的原样返回,以保持"拉不到就留空"的降级。 */
function formatDisplayTime(v: string | null | undefined): string | null {
  if (v == null || v === '') return null;
  const s = typeof v === 'string' ? v : String(v);
  // 支持 "YYYY-MM-DD[T ]HH:MM:SS(.fraction)?(Z|±HH:MM)?"
  const m = s.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})(?:\.(\d+))?/);
  if (!m) return s;
  const [, ymd, hms, frac] = m;
  // 毫秒:后端可能带 3~6 位;取前 3 位,不足 3 位末尾补 0。
  const ms = ((frac ?? '') + '000').slice(0, 3);
  return `${ymd} ${hms}.${ms}`;
}

/** 本地草稿:每 code 一份,localStorage 键 = draftKey(code)。 */
type FieldsDraft = ScenePreviewFields;

/** 云端控制事件提交前规整:剥 Form.List 的行 id + 剔无关字段,与 SceneBosFiles 语义一致。
 *  - 空/undefined:emptyAs='empty' → 返 []（清空 external.events）;'undefined' → 返 undefined（不改）。
 *  - once 模式:带 count,count>1 才带 frequency;while_inside 带 frequency。 */
function buildSubmitEvents(
  events?: SceneEvent[],
  emptyAs: 'undefined' | 'empty' = 'undefined',
): SceneEvent[] | undefined {
  if (!events || events.length === 0) {
    return emptyAs === 'empty' ? [] : undefined;
  }
  return events.map(ev => {
    const trigger: EventTrigger =
      ev.trigger.type === 'position'
        ? { type: 'position', position: ev.trigger.position, radius: ev.trigger.radius }
        : { type: 'time', time: ev.trigger.time };
    const out: SceneEvent = {
      trigger,
      mode: ev.mode,
      action: { type: ev.action.type, cloudCmd: ev.action.cloudCmd },
    };
    if (ev.mode === 'once') {
      out.count = ev.count;
      if (ev.count && ev.count > 1) out.frequency = ev.frequency;
    } else {
      out.frequency = ev.frequency;
    }
    return out;
  });
}

/** 每条 preview item 都有一个稳定的 draft key,批量入口按 code / 单场景入口按 sceneId 分区。 */
type EntryMode = 'code' | 'scene';
const draftKey = (mode: EntryMode, id: string | number) => `sim:annotateDraft:${mode}:${id}`;
/** 每条 item 的 draft key(item 附带 code + 可选 existing.sceneId);单场景入口下 code = 'scene:<id>'。 */
const itemDraftKey = (mode: EntryMode, it: ScenePreviewItem): string => {
  if (mode === 'scene' && it.existing) return draftKey('scene', it.existing.sceneId);
  return draftKey('code', it.code);
};

/** 拿到 preview 后,默认 draft = fields(或空);优先 localStorage(未提交前用户的编辑保留)。 */
function loadDraft(key: string, fromPreview: FieldsDraft | null): FieldsDraft {
  const empty: FieldsDraft = {
    sceneStartTime: null, sceneEndTime: null, issueTime: null,
    mapName: null, mapVersion: null, openSwitchClosedLoopSec: null,
    maskFinishSignal: null, cloudCmdPolicy: null, label: null,
    description: null,
    events: null,
  };
  try {
    const raw = localStorage.getItem(key);
    if (raw) return { ...empty, ...(fromPreview ?? {}), ...JSON.parse(raw) };
  } catch { /* JSON 坏了就走 preview */ }
  return { ...empty, ...(fromPreview ?? {}) };
}

// 注:saveDraftRaw 随 handleSaveDraft 一起在一期删除;二期恢复批量入口时再补回。
// clearDraftRaw 仍在 handleCancel / batch-update 成功后使用,保留。

function clearDraftRaw(key: string) {
  try { localStorage.removeItem(key); } catch { /* noop */ }
}

/** SceneListItem → ScenePreviewItem 适配器。
 *
 *  单场景编辑入口(?sceneId=)不走 preview 接口,直接把已有场景元数据搓成一条
 *  虚拟 preview item 灌进队列;下游渲染/编辑逻辑与批量入口共用。
 *
 *  场景基线接口另外提供文件与数据库共用的有效字段值（包括派生闭环秒数）。
 */
function sceneToPreviewItem(s: SceneListItem): ScenePreviewItem {
  return {
    code: s.apiCode || `#${s.id}`,
    // 单场景编辑走独立 source 分支(渲染时不显 jira / case_flow 蓝/青 tag)
    source: (s.source === 'jira' ? 'jira' : 'case_flow'),
    resolved: true,
    error: null,
    fields: {
      // 时间统一按 `YYYY-MM-DD HH:MM:SS.fff` 落进 draft(见 formatDisplayTime),
      // 编辑框 / diff 展示都以此为准;后端 SceneFieldPatch 接受该格式(_absolute_time)。
      sceneStartTime: formatDisplayTime(s.sceneStartTime),
      sceneEndTime: formatDisplayTime(s.sceneEndTime),
      // 故障时刻来自 external.jira.issue_time (Jira 侧维护,不可编辑,只读展示)。
      // 老场景 / 非 jira 来源为 null,前端渲染成 "-"。
      issueTime: formatDisplayTime(
        (s.external?.jira as { issue_time?: string | null } | undefined)?.issue_time ?? null,
      ),
      mapName: s.mapName ?? null,
      mapVersion: s.mapVersion ?? null,
      // 开环切闭环秒数落 external 顶层
      openSwitchClosedLoopSec: typeof s.external?.open_switch_closed_loop_sec === 'number'
        ? s.external.open_switch_closed_loop_sec as number
        : null,
      // 是否屏蔽 finish 信号落 external 顶层；缺省 / 脏值 → null（渲染默认不屏蔽）
      maskFinishSignal: typeof s.external?.mask_finish_signal === 'boolean'
        ? s.external.mask_finish_signal as boolean
        : null,
      // 云控指令合并策略落 external 顶层；缺省 / 脏值 → null（渲染默认 passthrough）
      cloudCmdPolicy: isCloudCmdPolicy(s.external?.cloud_cmd_policy) ? s.external?.cloud_cmd_policy : null,
      label: s.label ?? null,
      description: s.description ?? null,
      // 云端控制事件:来自 external.events(JSONB);老场景没有 → null,渲染为"未配置"。
      // 编辑态用 antd Form.List + EventListEditor 消费;保存时经 buildSubmitEvents 规整
      // 后走 SceneFieldPatch.events 落库。
      events: Array.isArray(s.external?.events)
        ? (s.external!.events as unknown as SceneEvent[])
        : null,
    },
    external: {
      // 走后端同域代理 (/api/scene/inspection/{id}/preview.webp) 绕开 BOS CORS,
      // 前端 fetch bytes 后逐帧解码播控。后端 previewUrl 只作为"webp 是否存在"的信号:
      // 有则挂代理地址,无则不挂 (标注页右侧走 Empty 占位)。
      ...(s.previewUrl ? { previewUrl: `/api/scene/inspection/${s.id}/preview.webp` } : {}),
      ...(s.sceneVersion ? { sceneVersion: s.sceneVersion } : {}),
      ...(s.securityLevel ? { securityLevel: s.securityLevel } : {}),
    },
    algorithmVersion: s.algorithmVersion ?? null,
    mapMissingReason: null,
    existing: {
      sceneId: s.id,
      sceneName: s.name,
      createdAt: s.createTime ?? null,
    },
  };
}

/** 构建子树的 TreeSelect 树形数据(目录灰色不可选仅导航,叶子可选)。纯函数放模块级,
 *  供组件内 useMemo 缓存引用稳定使用。 */
const buildSubtreeTreeData = (nodes: SceneTagNode[]): any[] => nodes.map((n) => ({
  key: n.id,
  value: n.id,
  title: tagDisplayName(n),
  disabled: n.nodeFlag !== 1,   // 目录置灰,仅叶子可点选
  selectable: n.nodeFlag === 1,
  children: n.children?.length ? buildSubtreeTreeData(n.children) : undefined,
}));

// ─── 小组件:字段行 ──────────────────────────────────

/** 可编辑字段行:视觉与 MetaItem 对齐 —— label 走同一份样式(13px 黑色),
 *  marginBottom 也和 MetaItem 一致(10)。切换只读/编辑态时字段行字号、
 *  相对位置、颜色都不跳。
 *  - hint: label 旁边的短注解(与 label 同一行),用于「清空 = 移除」这种非常短的补充。
 *  - description: 输入框下方的长解释(多行也可),说明字段语义 / 允许值 / 默认值等。 */
function FieldRow({ label, hint, description, required, children, origHint }: {
  label: string; hint?: string; description?: React.ReactNode; required?: boolean;
  children: React.ReactNode;
  origHint?: string;  // 编辑后展示 "原:xxx"
}) {
  return (
    <div className="sim-field-row" style={{ marginBottom: 10 }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 6,
        marginBottom: 4,
        fontSize: 13, color: '#1d1d1f', fontWeight: 500,
      }}>
        {required && <span style={{ color: '#ff4d4f' }}>*</span>}
        <span>{label}</span>
        {hint && <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>{hint}</Text>}
      </div>
      {children}
      {description && (
        <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c', lineHeight: 1.5 }}>
          {description}
        </div>
      )}
      {origHint && (
        <div style={{ marginTop: 4, fontSize: 11, color: '#d97706' }}>
          <EditOutlined /> 原:{origHint}
        </div>
      )}
    </div>
  );
}

function MetaItem({ label, value, mono = true }: {
  label: string; value: React.ReactNode; mono?: boolean;
}) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{
        fontSize: 13, color: '#1d1d1f', fontWeight: 500, marginBottom: 4,
      }}>{label}</div>
      <div style={{
        // 值容器高度对齐 antd Input(32px):切编辑态时字段行位置不跳。
        // 多行内容(如场景描述)靠 minHeight 兼容,超出自然向下伸展。
        // 值用灰褐色 (stone-500),与黑色 label 拉开层次。
        fontSize: 13, color: '#78716c',
        fontFamily: mono ? MONO : undefined,
        minHeight: 32,
        display: 'flex', alignItems: 'center',
        wordBreak: 'break-all',
        whiteSpace: 'pre-wrap',
      }}>
        {value ?? <span style={{ color: '#c9cdd4', fontFamily: 'inherit' }}>-</span>}
      </div>
    </div>
  );
}

/** 「场景信息」Tab 内三区块的分组标题(基础信息 / 高级参数 / 所属场景集)。 */
function SectionHeader({ title, style }: { title: string; style?: React.CSSProperties }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      marginBottom: 10, paddingBottom: 6,
      borderBottom: '1px solid #f0f0f0',
      fontSize: 13, fontWeight: 600, color: '#1d1d1f',
      ...style,
    }}>
      {title}
    </div>
  );
}

// ─── 主页面 ──────────────────────────────────────────

export default function SceneAnnotatePage() {
  const { message, notification, modal } = App.useApp();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // 两种入参:codes= (批量) 或 sceneId= (单场景);sceneId 优先
  const sceneIdParam = params.get('sceneId');
  const sceneId = sceneIdParam ? Number(sceneIdParam) : null;
  const mode: EntryMode = sceneId && Number.isFinite(sceneId) ? 'scene' : 'code';

  // 返回位置记忆:从场景管理(management)进来时带 nodeId + page,返回时回到原场景集+分页;
  // 从对比页/生成页/任务列表(compare/generation/job-tasks)进来时走 navigate(-1)
  // 复用浏览器历史,回到离开前那个页面的原位置(分页/滚动等由来源页自己的 URL 记忆恢复)。
  const fromRaw = params.get('from');
  const backFrom =
    fromRaw === 'generation' || fromRaw === 'compare' || fromRaw === 'job-tasks'
      ? fromRaw
      : 'management';
  const backNodeId = params.get('nodeId');
  const backPage = params.get('page');
  const goBackToSource = useCallback(() => {
    if (backFrom === 'generation' || backFrom === 'compare' || backFrom === 'job-tasks') {
      navigate(-1);
      return;
    }
    if (backNodeId) {
      const q = `nodeId=${backNodeId}${backPage ? `&page=${backPage}` : ''}`;
      navigate(`/scene-management?${q}`);
      return;
    }
    navigate('/scene-management');
  }, [backFrom, backNodeId, backPage, navigate]);

  // 另存为新场景 / 删除派生场景(从旧页 SceneBosFiles 迁入)
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [saveAsSubmitting, setSaveAsSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const codes = useMemo(() => {
    if (mode === 'scene') return [];
    const raw = params.get('codes') ?? '';
    return raw.split(/[\s,;;，]+/).map(c => c.trim()).filter(Boolean);
  }, [params, mode]);

  // 批次级选项 (只在批量 mode 生效;单场景 mode 走场景既有字段,不再展示):
  //  - source: 'jira' | 'case_flow',入口卡强制选定,previewScenes 用它而非 'auto'
  //  - confidential: 提交时透传 securityLevel=1
  //  - sceneSetNodeId: 提交时透传目标场景集
  // 兼容:入口卡老版可能没带 source(退化到 'case_flow');兼容 URL 手打场景。
  const batchSource: 'jira' | 'case_flow' = params.get('source') === 'jira' ? 'jira' : 'case_flow';
  const batchConfidential = params.get('confidential') === '1';
  const batchSceneSetNodeIdRaw = params.get('sceneSetNodeId');
  const batchSceneSetNodeId = batchSceneSetNodeIdRaw ? Number(batchSceneSetNodeIdRaw) : null;

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<ScenePreviewItem[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);

  // 每条的当前编辑值(内存),初始化时从 preview + localStorage 合并
  const [drafts, setDrafts] = useState<Record<string, FieldsDraft>>({});
  // 每条的 preview 原值,用于对比/展示 "原:xxx"
  const [originals, setOriginals] = useState<Record<string, FieldsDraft | null>>({});
  // 每条是否已经"保存草稿"过(打绿标);key = itemDraftKey(mode, item)
  const [savedKeys, setSavedKeys] = useState<Set<string>>(new Set());

  // ── scene mode 才用的三块附加数据(从原 SceneDetail 页搬过来) ─
  // 元信息 KV 数据源:完整 SceneListItem(比 ScenePreviewItem 字段多)
  const [sceneMeta, setSceneMeta] = useState<SceneListItem | null>(null);
  // ── 场景标签体系(scene-tag 关联):标签树每个根节点各映射一个可编辑位置,单根单选一个叶子 ─
  // assigned:场景已挂的叶子标签(编辑态为暂存值,保存时与快照差分提交)
  const [assignedTags, setAssignedTags] = useState<SceneTagNode[]>([]);
  // 全量标签树
  const [sceneTreeTags, setSceneTreeTags] = useState<SceneTagNode[]>([]);
  // 进入编辑时的标签快照:保存时用差分计算 batch-add/delete,取消时还原
  const [tagSnapshot, setTagSnapshot] = useState<SceneTagNode[] | null>(null);
  // BOS 配置文件浏览器
  const [bosPrefix, setBosPrefix] = useState('');
  const [bosFiles, setBosFiles] = useState<SceneBosConfigFileItem[]>([]);
  const [bosLoading, setBosLoading] = useState(false);
  const [bosSelectedKey, setBosSelectedKey] = useState<string | null>(null);
  const [bosFileContent, setBosFileContent] = useState<SceneBosConfigFileContent | null>(null);
  const [bosContentLoading, setBosContentLoading] = useState(false);
  // 更新记录 = 场景版本历史(RFC scene-version-design 一期):从 GET /scenes/:id/versions
  // 拉数据,每条带 versionNo/source/versionBosKey/changedFields;渲染时把 rollback
  // 按钮内嵌到每条。老 log(scene_version=NULL,PR-1 前)兜底排后。
  // 分页:默认 pageSize=5,前端翻页时不 reset 其它场景 state。
  const HISTORY_PAGE_SIZE = 5;
  const [historyLogs, setHistoryLogs] = useState<SceneVersion[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyCurrent, setHistoryCurrent] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  // 场景所属场景集列表 (scene mode 才拉);仅目录节点隐藏子树内的场景集不显
  const [sceneSets, setSceneSets] = useState<SceneSetOfScene[]>([]);
  const [sceneSetsLoading, setSceneSetsLoading] = useState(false);
  // 缩略图 webp 解码失败 → 直接不显示缩略图 Card
  const [previewBroken, setPreviewBroken] = useState(false);
  // 编辑态:默认只读展示,点「编辑」进入 editing=true,字段变为可写输入框。
  // 保存/取消都会退出 editing 状态并恢复只读视图。
  const [editing, setEditing] = useState(false);
  // 保存请求 in-flight 标记,防重复点
  const [saving, setSaving] = useState(false);
  // 数据重拉计数:保存成功后 +1,让下面所有 useEffect(依赖了它)重跑,
  // 场景元数据 / BOS 文件 / 更新记录 / 所属场景集 一并刷新到最新值。
  const [reloadTick, setReloadTick] = useState(0);
  // 历史标签下拉候选:进入编辑态时按需拉取一次(后端 Redis 缓存 3h,反复进出也很轻);
  // 拉不到 / 出错都不阻断编辑,输入框仍可自由输入。
  const [labelOptions, setLabelOptions] = useState<string[]>([]);
  // 正在回退的目标 log id;防重复点 + 展示 loading。null=空闲。
  // (RFC scene-version-design 一期 PR-3;版本历史与更新记录已合并为一份,直接嵌
  //  在更新记录卡里逐条渲染"回退到此"按钮。)
  const [rollingBackLogId, setRollingBackLogId] = useState<number | null>(null);
  // 云端控制事件的 antd Form 实例(仅承载 Form.List name="events");
  // 复用 SceneBosFiles 的 EventListEditor,后者依赖外层 <Form> + Form.useWatch。
  const [eventsForm] = Form.useForm<{ events: SceneEvent[] }>();
  const contentDraft = useSceneContentDraft();
  const [startingEdit, setStartingEdit] = useState(false);
  const startingEditRef = useRef(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [retrySaveRequest, setRetrySaveRequest] = useState<SceneContentRequest | null>(null);
  const [activeTab, setActiveTab] = useState('info');
  const [generationBusy, setGenerationBusy] = useState(false);
  // 硬失败提示（R2）：status=9 / 最近一次生成任务崩了 → 顶部「当前场景异常」Alert。
  // 口径全在后端 scene_health，前端只做展示。
  const [sceneHealth, setSceneHealth] = useState<SceneHealth | null>(null);
  const [generationTasks, setGenerationTasks] = useState<SceneContentBaseline['generationTasks']>([]);
  const [checkingGeneration, setCheckingGeneration] = useState(true);
  const [generationCheckError, setGenerationCheckError] = useState<string | null>(null);
  const [draftNeedsReload, setDraftNeedsReload] = useState(false);
  const [effectiveFields, setEffectiveFields] = useState<SceneFieldPatch | null>(null);
  const latestEditState = useRef({ editing, saving, baseVersion: contentDraft.baseline?.baseVersion });
  latestEditState.current = { editing, saving, baseVersion: contentDraft.baseline?.baseVersion };
  const baselineReadSequence = useRef(0);
  // 生成结束（busy true→false）时版本快照会被重打成同号新内容（R1 沿用链路），
  // 版本号不变所以不能靠 reloadTick；用它单独触发「更新记录 + 文件列表」重拉。
  const prevBusyRef = useRef(false);
  const [versionRefreshTick, setVersionRefreshTick] = useState(0);
  // 「回退到历史版本」按钮：滚动定位到更新记录卡片并短暂高亮。
  const historyCardRef = useRef<HTMLDivElement | null>(null);
  const [historyHighlight, setHistoryHighlight] = useState(false);
  // 豁免（9 → 7 瑕疵品）：与「场景生成任务详情」面板共用同一接口。
  const [waiveOpen, setWaiveOpen] = useState(false);
  const [waiveReason, setWaiveReason] = useState('');
  const [waiving, setWaiving] = useState(false);
  // 「重新生成」逃生门（R3 形态 B）：生成任务崩了 / 取消会让场景被永久锁住编辑与回退
  // （CF 可能仍在写文件，属既有安全设计），这条路径不判锁，是失败场景唯一的恢复路径。
  const [regenerating, setRegenerating] = useState(false);
  // 「重新构建」：用源数据重跑一次场景生成，与场景列表的批量构建同一个入口
  // （/dispatch/by-ids → source='rebuild'）。
  const [rebuilding, setRebuilding] = useState(false);
  // setState 是异步的，连点两下时两次回调读到的是同一个 rebuilding=false —— 用 ref 做
  // 同步闸门，保证一次点击只发一个 Job（后端没有幂等键，重复派发真的会建两个 Job）。
  const rebuildingRef = useRef(false);
  const editBlocked = generationBusy || checkingGeneration || !!generationCheckError || draftNeedsReload || !!retrySaveRequest;
  const fieldsReadOnly = saving || editBlocked || contentDraft.editMode === 'files';
  const filesReadOnly = saving || editBlocked || contentDraft.editMode === 'fields';
  const bosReadSequence = useRef(0);
  const currentSceneId = useRef(sceneId);
  currentSceneId.current = sceneId;

  // 初次 mount:批量走 previewScenes;单场景走 getSceneInspectionList + 适配器
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      // 切换场景时重置 preview 缩略图状态,避免上一个 sceneId 的 broken flag 泄漏
      setPreviewBroken(false);
      setSceneMeta(null);
      setSceneHealth(null);
      setWaiveOpen(false);
      setWaiveReason('');
      prevBusyRef.current = false;
      // 切场景/入参 → 一律退回只读态
      setEditing(false);
      setDraftNeedsReload(false);
      setEffectiveFields(null);
      contentDraft.reset();
      // 分类标签暂存状态随场景切换一起清空(快照由标签加载 effect 重新装载)
      setTagSnapshot(null);
      setAssignedTags([]);
      bosReadSequence.current += 1;
      setSaveError(null);
      setRetrySaveRequest(null);
      try {
        let list: ScenePreviewItem[] = [];
        if (mode === 'scene' && sceneId) {
          const resp = await getSceneInspectionList({
            ids: [sceneId], pageSize: 1, includeDisabled: true,
          });
          // 后端 list 项字段丰度大于 SceneInspectionItem 类型定义,当 SceneListItem 消费
          const one = (resp.list?.[0] as unknown as SceneListItem) ?? null;
          if (one) {
            list = [sceneToPreviewItem(one)];
            if (alive) setSceneMeta(one);
          }
        } else if (codes.length > 0) {
          const resp = await previewScenes(codes, batchSource);
          list = resp.items ?? [];
        }
        if (!alive) return;
        setItems(list);
        const nextDrafts: Record<string, FieldsDraft> = {};
        const nextOrig: Record<string, FieldsDraft | null> = {};
        const nextSaved = new Set<string>();
        for (const it of list) {
          const key = itemDraftKey(mode, it);
          nextOrig[it.code] = it.fields ?? null;
          nextDrafts[it.code] = loadDraft(key, it.fields ?? null);
          if (localStorage.getItem(key)) nextSaved.add(key);
        }
        setOriginals(nextOrig);
        setDrafts(nextDrafts);
        setSavedKeys(nextSaved);
      } catch (err) {
        console.error('加载场景数据失败:', err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, sceneId, codes.join(','), batchSource, reloadTick]);

  // 单场景:加载已挂叶子标签 + 全量标签树(编辑态选择器用)。
  useEffect(() => {
    if (mode !== 'scene' || !sceneId) return;
    let alive = true;
    void getSceneTagsOfScene(sceneId)
      .then((tags) => { if (alive) setAssignedTags(tags); })
      .catch((err) => console.error('加载场景标签失败:', err));
    void getSceneTagTree()
      .then((tree) => { if (alive) setSceneTreeTags(tree); })
      .catch((err) => console.error('加载标签树失败:', err));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, sceneId]);

  /** 收集子树下全部叶子标签。 */
  const collectLeafTags = (n: SceneTagNode): SceneTagNode[] => {
    if (n.nodeFlag === 1) return [n];
    return (n.children || []).flatMap(collectLeafTags);
  };

  /** 标签树全部根节点(scene-tag/tree 顶层数组,parentId=-1):不再硬编码四大分类,
   *  每个根各映射一个可编辑位置;caseflow 同步来的新根(场景仿真/AD算法/…)随树自动进入。 */
  const rootTags = sceneTreeTags;

  /** 每个根节点 → TreeSelect treeData 的缓存:整棵树可能有上千节点(AD算法 300+ 叶子),
   *  渲染/输入时避免每次重建全部子树;treeData 引用稳定,TreeSelect 内部缓存也命中。 */
  const rootTreeDataMap = useMemo(() => {
    const map = new Map<number, any[]>();
    for (const root of rootTags) map.set(root.id, buildSubtreeTreeData([root]));
    return map;
  }, [rootTags]);

  /** 点击标签 chip 的浮层:英文键 + 中文别名 + 创建人/创建时间(同位置展示)。 */
  const tagInfoTitle = (t: SceneTagNode): React.ReactNode => (
    <div style={{ lineHeight: 1.8 }}>
      <div>英文键：{t.tagKey || '-'}</div>
      <div>中文别名：{t.alias || '-'}</div>
      <div>创建人：{t.creator || '-'}</div>
      <div>创建时间：{t.createTime ? String(t.createTime).replace('T', ' ').slice(0, 19) : '-'}</div>
    </div>
  );

  /** 某根节点下当前场景已选的叶子标签。 */
  const assignedTagOfRoot = (root: SceneTagNode): SceneTagNode | undefined => {
    const leafIds = new Set(collectLeafTags(root).map((l) => l.id));
    return assignedTags.find((t) => leafIds.has(t.id));
  };

  /** 变更某根节点下的标签(本地暂存):同一根只保留一个叶子。保存时才提交。 */
  const setRootTag = (root: SceneTagNode, newLeafId: number | undefined) => {
    if (tagSnapshot === null) return;  // 非编辑态不响应
    const leaves = collectLeafTags(root);
    const oldLeaf = leaves.find((l) => assignedTags.some((a) => a.id === l.id));
    if (oldLeaf && oldLeaf.id === newLeafId) return;
    setAssignedTags(prev => {
      const next = prev.filter((a) => a.id !== oldLeaf?.id);
      if (newLeafId != null) {
        const node = leaves.find((l) => l.id === newLeafId);
        if (node) next.push(node);
      }
      return next;
    });
  };

  /** 本地暂存与编辑时快照相比是否有变化(驱动保存按钮/离开拦截)。 */
  const hasTagChanges = useMemo(() => {
    if (!tagSnapshot) return false;
    const a = assignedTags.map((t) => t.id).sort().join(',');
    const b = tagSnapshot.map((t) => t.id).sort().join(',');
    return a !== b;
  }, [tagSnapshot, assignedTags]);

  /** 提交暂存标签差分(batch-delete 旧 + batch-add 新)。 */
  const applyTagChanges = async () => {
    if (!sceneId || !tagSnapshot) return;
    const snapshotIds = new Set(tagSnapshot.map((t) => t.id));
    const currentIds = new Set(assignedTags.map((t) => t.id));
    const toAdd = assignedTags.filter((t) => !snapshotIds.has(t.id));
    const toRemove = tagSnapshot.filter((t) => !currentIds.has(t.id));
    for (const t of toRemove) {
      await batchDeleteScenesFromTag(t.id, [sceneId]);
    }
    for (const t of toAdd) {
      await batchAddScenesToTag(t.id, [sceneId]);
    }
    setTagSnapshot(null);
  };

  // The scene status can finish before its compute task. Poll the backend's
  // task-aware baseline and keep interrupted drafts blocked after the task ends.
  useEffect(() => {
    if (mode !== 'scene' || !sceneId) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setCheckingGeneration(true);
    const poll = async () => {
      // An explicit edit/reload request owns its response. Background polling
      // must not invalidate it when the network takes longer than one interval.
      if (startingEditRef.current) {
        timer = setTimeout(poll, 5000);
        return;
      }
      const sequence = ++baselineReadSequence.current;
      try {
        const base = await getSceneContentBaseline(sceneId, true);
        if (!alive || sequence !== baselineReadSequence.current) return;
        setGenerationBusy(base.generationBusy);
        setGenerationTasks(base.generationTasks ?? []);
        setGenerationCheckError(base.generationStatusError ?? null);
        setSceneHealth(base.sceneHealth ?? null);
        if (prevBusyRef.current && !base.generationBusy) {
          // 一次生成刚结束：沿用行（R1）内容与版本快照都换了，重拉更新记录 + 文件列表。
          setVersionRefreshTick(t => t + 1);
        }
        prevBusyRef.current = base.generationBusy;
        const state = latestEditState.current;
        if (state.editing && !state.saving && state.baseVersion != null) {
          const baseVersion = state.baseVersion;
          setDraftNeedsReload(previous => requiresDraftReload(previous, base.generationBusy, baseVersion, base.baseVersion));
        } else if (!state.editing) {
          setEffectiveFields(base.fields);
        }
      } catch (error) {
        if (alive && sequence === baselineReadSequence.current) {
          setGenerationCheckError(error instanceof Error ? error.message : '无法确认场景生成状态');
        }
      } finally {
        if (alive) {
          setCheckingGeneration(false);
          timer = setTimeout(poll, 5000);
        }
      }
    };
    void poll();
    return () => { alive = false; clearTimeout(timer); };
  }, [mode, sceneId, reloadTick]);

  // scene mode 额外拉:BOS 配置文件列表 + 更新记录 (与 preview 并行)
  useEffect(() => {
    if (mode !== 'scene' || !sceneId) return;
    let alive = true;
    setBosLoading(true);
    setBosSelectedKey(null);
    setBosFileContent(null);
    setBosFiles([]);
    setBosPrefix('');
    getSceneBosConfigFiles(sceneId)
      .then(data => {
        if (!alive) return;
        setBosPrefix(data?.prefix ?? '');
        setBosFiles(Array.isArray(data?.files) ? data.files : []);
      })
      .catch(err => { console.error('BOS 文件列表加载失败:', err); })
      .finally(() => { if (alive) setBosLoading(false); });
    return () => { alive = false; };
  }, [mode, sceneId, reloadTick, versionRefreshTick]);

  // 切场景 / 强制 reload 时回到第一页(不影响手动翻页拉数据的 effect)。
  useEffect(() => {
    setHistoryCurrent(1);
  }, [mode, sceneId, reloadTick]);

  useEffect(() => {
    if (mode !== 'scene' || !sceneId) return;
    let alive = true;
    setHistoryLoading(true);
    setHistoryLogs([]);
    // 切场景/reload 时回到第一页;否则跟随 historyCurrent
    getSceneVersions(sceneId, { current: historyCurrent, pageSize: HISTORY_PAGE_SIZE })
      .then(page => {
        if (!alive) return;
        setHistoryLogs(page.items);
        setHistoryTotal(page.total);
      })
      .catch(err => { console.error('更新记录加载失败:', err); })
      .finally(() => { if (alive) setHistoryLoading(false); });
    return () => { alive = false; };
  }, [mode, sceneId, reloadTick, historyCurrent, versionRefreshTick]);

  useEffect(() => {
    if (mode !== 'scene' || !sceneId) return;
    let alive = true;
    setSceneSetsLoading(true);
    setSceneSets([]);
    getSceneSetsOfScene(sceneId)
      .then(sets => { if (alive) setSceneSets(sets); })
      .catch(err => { console.error('场景集列表加载失败:', err); })
      .finally(() => { if (alive) setSceneSetsLoading(false); });
    return () => { alive = false; };
  }, [mode, sceneId, reloadTick]);

  // 历史标签下拉候选:仅在编辑态按需拉一次(且当下候选为空时才请求)。
  // 后端 Redis 缓存 3h,失败不阻断编辑。
  useEffect(() => {
    if (!editing || labelOptions.length > 0) return;
    let alive = true;
    getSceneLabels()
      .then(labels => { if (alive) setLabelOptions(labels); })
      .catch(err => { console.error('场景标签候选加载失败:', err); });
    return () => { alive = false; };
  }, [editing, labelOptions.length]);

  // ── 另存为 / 删除派生场景（从旧页 SceneBosFiles 迁入）────────────────
  // ⚠️ 这些 hooks 必须在下方 early-return（mode==='code' / loading / 空态）之前调用。
  // 当前场景是否为「另存为」派生场景(external.derived_from);true 才显示删除按钮。
  const isDerived = !!(sceneMeta?.external && (sceneMeta.external as Record<string, unknown>).derived_from);
  // 「重新构建」高亮口径：非终态 = 已录入(1) / 质检中(5)——还没走完「生成 + 质检」流程，
  // 这类场景需要重新构建，按钮高亮提示；已完成 / 已完成-瑕疵品 / 不合格 不高亮但照常可点。
  const needsRebuild = sceneMeta?.status === 1 || sceneMeta?.status === 5;
  // 「重新构建」不跟编辑态共用门禁（不能用 editBlocked）：场景文件尚未生成（baseline 409 →
  // generationCheckError）、草稿过期这类「不能编辑」的理由，恰恰是「该重新构建」的理由 ——
  // 挂上 editBlocked 会让最需要重建的场景按钮变灰。只拦真有写入在途 / 正在确认状态的时刻。
  // 构建在跑也不拦：撞链路由后端 needConfirm 兜底，卡死的生成锁本来就靠重建恢复。
  const rebuildBlocked = saving || checkingGeneration || !!retrySaveRequest;

  /**
   * 「另存为新场景」(F1):复用编辑态 eventsForm 的 events(single source of truth),
   * 叠加 newName + 目标树节点提交。基准场景不变。
   */
  const handleSaveAs = useCallback(
    async (values: { newName?: string; targetTreeNodeId: number; description?: string }) => {
      if (sceneId == null || !Number.isFinite(sceneId) || editBlocked || contentDraft.filesDirty) return;
      let formEvents: SceneEvent[] | undefined;
      try {
        const fv = await eventsForm.validateFields();
        formEvents = fv.events;
      } catch {
        return; // events 行级校验未过,保持 modal 打开让用户回去改
      }
      setSaveAsSubmitting(true);
      try {
        const created = (await saveAsScene(sceneId, {
          newName: values.newName,
          targetTreeNodeId: values.targetTreeNodeId,
          events: buildSubmitEvents(formEvents),
          description: values.description,
        })) as { name?: string } | undefined;
        const newName = created?.name || values.newName || '新场景';
        message.success(`已另存为新场景「${newName}」`);
        setSaveAsOpen(false);
      } catch {
        /* axios 拦截器已提示(非叶子/重名/基准不存在等),保持 modal 让用户重试 */
      } finally {
        setSaveAsSubmitting(false);
      }
    },
    [sceneId, eventsForm, message, editBlocked, contentDraft.filesDirty],
  );

  /**
   * 硬删除当前派生场景(物理删 DB + BOS + 树关联,不可恢复)。
   * 仅在 isDerived 时可触发;成功后返回来源列表页(回原场景集+分页)。
   */
  const handleDelete = useCallback(async () => {
    if (sceneId == null || !Number.isFinite(sceneId) || editBlocked) return;
    setDeleting(true);
    try {
      const res = await deleteDerivedScenes([sceneId]);
      if (res?.affected?.includes(sceneId)) {
        message.success('已删除该派生场景');
        goBackToSource();
      } else if (res?.notDerived?.includes(sceneId)) {
        message.error('该场景不是派生场景,不能删除');
      } else {
        message.error('删除失败:场景不存在或已被删除');
      }
    } catch {
      /* axios 拦截器已提示 */
    } finally {
      setDeleting(false);
    }
  }, [sceneId, message, goBackToSource, editBlocked]);

  const openBosFile = async (row: SceneBosConfigFileItem) => {
    if (!sceneId) return;
    const sequence = ++bosReadSequence.current;
    setBosSelectedKey(row.key);
    setBosContentLoading(true);
    setBosFileContent(null);
    try {
      const policy = contentDraft.baseline?.files.find(file => file.key === row.key);
      if (editing && policy?.editable) {
        await contentDraft.load(row.key);
      } else {
        const result = await getSceneBosConfigFileContent(sceneId, row.key);
        if (sequence === bosReadSequence.current) setBosFileContent(result ?? null);
      }
    } catch (error) {
      if (sequence === bosReadSequence.current) setSaveError(error instanceof Error ? error.message : '文件加载失败');
    } finally {
      if (sequence === bosReadSequence.current) setBosContentLoading(false);
    }
  };

  // ⚠️ Hooks 必须在任何 early return 之前调用(React hooks 规则:不能条件调用)。
  // 只改 events 时也要能触发 dirty,让"保存"亮起 → 用 Form.useWatch 订阅 eventsForm。
  // useWatch 在 form 尚未有值时返回 undefined,不影响下面 loading / empty 分支的渲染。
  const watchedEvents = Form.useWatch<SceneEvent[] | undefined>('events', eventsForm);
  const hasUnsavedChanges = editing && (contentDraft.dirty || hasTagChanges);
  const blocker = useBlocker(hasUnsavedChanges);
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const beforeUnload = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [hasUnsavedChanges]);
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    const dialog = modal.confirm({
      title: saving ? '正在保存场景' : '有未保存的修改',
      content: saving ? '请等待保存完成后再离开。' : '离开后将丢弃本次场景信息和文件修改。',
      okText: saving ? '继续等待' : '丢弃并离开', cancelText: '继续编辑',
      onOk: () => saving ? blocker.reset() : blocker.proceed(),
      onCancel: () => blocker.reset(),
    });
    return () => dialog.destroy();
  }, [blocker, modal, saving]);

  // ── 无入参 / 加载 / 空态 ────────────────────
  // 一期后端 annotate-submit 未落地,「批量标注」(code mode)链路暂不对外开放。
  // 单场景编辑(scene mode)走统一 content 接口。二期就绪后放开本分支即可。
  if (mode === 'code') {
    return (
      <div style={{ padding: 40 }}>
        <Empty description="批量场景标注功能暂未开放;单场景编辑请从场景列表点场景名进入" />
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <Button onClick={() => navigate('/scene-management')}>返回场景管理</Button>
        </div>
      </div>
    );
  }
  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;
  }
  if (items.length === 0) {
    return (
      <div style={{ padding: 40 }}>
        <Empty description={mode === 'scene' ? `场景 #${sceneId} 不存在或已删除` : 'preview 返回为空,请重试'} />
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <Button onClick={() => {
            if (mode === 'scene') goBackToSource();
            else navigate('/scene-generation');
          }}>返回</Button>
        </div>
      </div>
    );
  }

  const cur = items[currentIdx];
  const curKey = itemDraftKey(mode, cur);
  const draft = drafts[cur.code] ?? loadDraft(curKey, cur.fields);
  const orig = originals[cur.code] ?? null;
  const dirty = contentDraft.dirty;
  const savedInLocal = savedKeys.has(curKey);
  const normalizedOriginal = { ...orig, events: buildSubmitEvents(orig?.events ?? [], 'empty') ?? [] };

  // Programmatic file previews never claim ownership of the information form.
  const setField = <K extends keyof FieldsDraft>(k: K, v: FieldsDraft[K]) => {
    if (fieldsReadOnly) return;
    const next = { ...draft, [k]: v };
    const fields = fieldPatch({ ...next, events: buildSubmitEvents(watchedEvents ?? draft.events ?? [], 'empty') ?? [] }, normalizedOriginal);
    setDrafts(prev => ({ ...prev, [cur.code]: next }));
    contentDraft.syncFields({ [k]: v } as SceneFieldPatch, Object.keys(fields).length > 0);
  };

  const applyEditBaseline = (base: SceneContentBaseline) => {
    setGenerationBusy(base.generationBusy);
    setGenerationTasks(base.generationTasks ?? []);
    setCheckingGeneration(false);
    setGenerationCheckError(base.generationStatusError ?? null);
    if (base.generationBusy || base.generationStatusError) {
      if (editing) setDraftNeedsReload(true);
      setSaveError(base.generationStatusError || '场景正在生成或生成任务待处理，请查看下方任务信息，等待完成后编辑。');
      return;
    }
    setSceneMeta(prev => prev ? { ...prev, sceneVersion: base.baseVersion } : prev);
    const fresh = { ...draft, ...base.fields, events: base.fields.events ?? [] };
    setDrafts(prev => ({ ...prev, [cur.code]: fresh }));
    setOriginals(prev => ({ ...prev, [cur.code]: fresh }));
    eventsForm.setFieldsValue({ events: structuredClone(fresh.events) });
    contentDraft.reset(base);
    setRetrySaveRequest(null);
    setEffectiveFields(base.fields);
    setDraftNeedsReload(false);
    bosReadSequence.current += 1;
    setBosContentLoading(false);
    setBosSelectedKey(null);
    setBosFileContent(null);
    setBosFiles(base.files);
    setSaveError(null);
    // 进入编辑:给分类标签建快照(保存差分 / 取消还原)
    setTagSnapshot(structuredClone(assignedTags));
    setEditing(true);
  };

  const handleStartEdit = async () => {
    if (!sceneId || startingEditRef.current) return;
    startingEditRef.current = true;
    setStartingEdit(true);
    const sequence = ++baselineReadSequence.current;
    try {
      const base = await getSceneContentBaseline(sceneId);
      if (currentSceneId.current !== sceneId || sequence !== baselineReadSequence.current) return;
      applyEditBaseline(base);
    } catch (error) {
      if (currentSceneId.current === sceneId && sequence === baselineReadSequence.current) {
        setSaveError(error instanceof Error ? error.message : '无法进入编辑');
      }
    } finally {
      startingEditRef.current = false;
      setStartingEdit(false);
    }
  };

  const handleCancel = () => {
    if (orig) setDrafts(prev => ({ ...prev, [cur.code]: structuredClone(orig) }));
    eventsForm.setFieldsValue({ events: structuredClone(orig?.events ?? []) });
    contentDraft.reset();
    // 分类标签:还原到编辑时快照
    if (tagSnapshot) {
      setAssignedTags(structuredClone(tagSnapshot));
      setTagSnapshot(null);
    }
    bosReadSequence.current += 1;
    setBosSelectedKey(null);
    setBosFileContent(null);
    setBosContentLoading(false);
    setSaveError(null);
    clearDraftRaw(curKey);
    setEditing(false);
    setDraftNeedsReload(false);
    setReloadTick(tick => tick + 1);
    setRetrySaveRequest(null);
    message.info(retrySaveRequest ? '已退出编辑，请刷新确认之前提交的保存结果' : '已取消本次场景信息和文件修改');
  };

  // 注:handleSaveDraft / handleReset 已随「独立编辑卡 + 统一提交」链路一起删除;
  // 二期恢复批量标注入口时需要再补:
  //   - handleSaveDraft: saveDraftRaw + savedKeys 更新 + 可选切到下一条
  //   - handleReset:     drafts[cur.code] 清回 preview 初值 + 删 localStorage

  /** 回退当前场景到某个历史版本(PR-3)。
   *  语义:后端 POST /scenes/:id/rollback,不删后续版本,建新版本。成功后触发
   *  reloadTick +1,场景元数据 / 更新记录 / 版本列表全部重拉。 */
  const handleRollback = async (target: SceneVersion) => {
    if (!sceneId || editing || saving || editBlocked || target.isCurrent || !target.versionBosKey || target.sourceRef?.requiresRegeneration) return;
    setRollingBackLogId(target.logId);
    try {
      const newVer = await rollbackSceneToVersion(sceneId, target.logId);
      message.success(`已回退到 v${target.versionNo ?? '?'}(新版本 v${newVer.versionNo ?? '?'})`);
      setReloadTick(t => t + 1);
    } catch (err) {
      console.error('[rollback] 请求失败', err);
      message.error('回退失败,请重试');
    } finally {
      setRollingBackLogId(null);
    }
  };

  /** 「回退到历史版本」：本页回退按钮在更新记录卡片里，这里只负责导航过去。 */
  const handleGotoHistory = () => {
    setHistoryCurrent(1);
    setVersionRefreshTick(t => t + 1);
    window.requestAnimationFrame(() => {
      historyCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHistoryHighlight(true);
      window.setTimeout(() => setHistoryHighlight(false), 2000);
    });
  };

  /** 豁免不合格场景（9 → 7 瑕疵品）：与「场景生成任务详情」面板共用同一接口。
   *  失败（如必备文件缺失 / 状态已变）把后端 message 显示在 Alert 里，不弹空窗。 */
  const handleWaive = async () => {
    if (!sceneId) return;
    setWaiving(true);
    try {
      const result = await waiveScene(sceneId, waiveReason.trim() || undefined);
      const tips = ['已豁免为瑕疵品'];
      if (result.autoAddedToNodeName) tips.push(`并已加入场景集「${result.autoAddedToNodeName}」`);
      if (result.releasedDisable) tips.push('已解除禁用');
      message.success(tips.join('，'));
      setWaiveOpen(false);
      setWaiveReason('');
      setReloadTick(t => t + 1);
    } catch (e) {
      setWaiveOpen(false);
      setSaveError((e as { message?: string })?.message || '豁免失败');
    } finally {
      setWaiving(false);
    }
  };

  /** 「重新生成」（R3 形态 B 逃生门）：生成任务异常收场时场景既被禁用、又被生成锁挡住
   *  编辑与回退（CF 可能仍在写文件，是既有安全设计）。``/scene-creation/dispatch/by-ids``
   *  是**生成**入口、不判锁，也是后端唯一放行禁用场景的生成路径；跑完成功后端会自动解除
   *  禁用（``apply_resolved_time`` 的释放点）。force=true 越过「已有生成任务在跑」的拦截。 */
  const handleRegenerate = async () => {
    if (!sceneId || regenerating) return;
    setRegenerating(true);
    try {
      const result = await batchGenerateSceneByIds([sceneId], true);
      if (result?.error) {
        notification.warning({ message: '已发起重新生成', description: String(result.error), duration: 10 });
      } else {
        message.success('已发起重新生成');
      }
      setReloadTick(t => t + 1);
    } catch (e) {
      message.error((e as { message?: string })?.message || '发起重新生成失败');
    } finally {
      setRegenerating(false);
    }
  };

  /** 「重新构建」：用源数据重跑一次场景生成（与场景列表的批量构建同一入口）。
   *  force=false 那一次**本身就是发起**：没有链路碰撞时后端当场建好 Job（并按 rebuild 档
   *  记版本）；只有上一次构建还在跑时才返回 needConfirm（**不建 Job**），确认后再 force=true
   *  重发（latest-wins 顶替）。所以正常路径绝不能再补一次 force=true —— 那会一发两个 Job
   *  （对齐 FullSceneList 的 handleBatchBuild）。 */
  const handleRebuild = async () => {
    if (!sceneId || rebuildingRef.current) return;
    rebuildingRef.current = true;
    const reportDispatch = (result: { error?: unknown } | undefined) => {
      if (result?.error) {
        notification.warning({ message: '已发起重新构建', description: String(result.error), duration: 10 });
      } else {
        message.success('已发起重新构建');
      }
      setReloadTick(t => t + 1);
    };
    setRebuilding(true);
    try {
      const result = await batchGenerateSceneByIds([sceneId]);
      if (result?.needConfirm) {
        // 这次没建 Job；确认框的 onOk 在本次 try 之外执行，错误自己吞掉，
        // 否则会变成未处理的 rejection。
        modal.confirm({
          title: '上一次构建还在进行中',
          content: result.message || '该场景上一次构建还未完成，确认将终止上一次构建并重新发起？',
          okText: '仍要重新发起', cancelText: '取消',
          onOk: () => batchGenerateSceneByIds([sceneId], true)
            .then(reportDispatch)
            .catch(e => message.error((e as { message?: string })?.message || '发起重新构建失败')),
        });
        return;
      }
      reportDispatch(result);
    } catch (e) {
      message.error((e as { message?: string })?.message || '发起重新构建失败');
    } finally {
      rebuildingRef.current = false;
      setRebuilding(false);
    }
  };

  const handleSubmitAll = async (retryRequest?: SceneContentRequest) => {
    if (!sceneId || saving || !editing || (!retryRequest && (!(dirty || hasTagChanges) || editBlocked))) return;
    setSaving(true);
    setSaveError(null);
    let submitted = retryRequest;
    try {
      // 1) 分类标签差分先落库(只在新提交时;retry 时第一次尝试已提交过)
      if (hasTagChanges) {
        await applyTagChanges();
      }
      // 2) 只改了分类标签、字段内容无变化 → 轻保存,退出编辑态即可
      if (!submitted && !dirty && !editBlocked) {
        contentDraft.reset();
        setEditing(false);
        setDraftNeedsReload(false);
        setReloadTick(tick => tick + 1);
        message.success('分类标签已保存');
        return;
      }
      if (!submitted) {
        // File parsing may update the read-only form preview. Never validate or
        // submit that preview as a second user input.
        const values = contentDraft.editMode === 'fields' ? await eventsForm.validateFields() : null;
        const fields = values ? fieldPatch(
          { ...draft, events: buildSubmitEvents(values.events ?? [], 'empty') ?? [] }, normalizedOriginal,
        ) : {};
        if (contentDraft.editMode === 'fields' && timeRangeChanged(fields, normalizedOriginal)) {
          const confirmed = await new Promise<boolean>(resolve => modal.confirm({
            title: '修改时间将重新生成场景',
            content: '重新生成可能覆盖之前人工修改的场景文件。系统会保留生成前的旧版本，任务结束前不能编辑此场景。',
            okText: '保存并重新生成', cancelText: '继续编辑',
            onOk: () => resolve(true), onCancel: () => resolve(false),
          }));
          if (!confirmed) return;
        }
        submitted = contentDraft.request(fields);
      }
      const result = await saveSceneContent(sceneId, submitted);
      if (!result || typeof result.sceneVersion !== 'number') throw new Error('未收到保存成功的确认，请重试');
      contentDraft.reset();
      setRetrySaveRequest(null);
      clearDraftRaw(curKey);
      setSavedKeys(prev => { const next = new Set(prev); next.delete(curKey); return next; });
      // 分类标签差分已在提交前置处理中完成(若无标签改动则丢弃快照)
      setTagSnapshot(null);
      setEditing(false);
      setDraftNeedsReload(false);
      bosReadSequence.current += 1;
      setBosSelectedKey(null);
      setBosFileContent(null);
      setReloadTick(tick => tick + 1);
      if (result.dispatch?.error) {
        notification.warning({ message: '场景已保存', description: result.dispatch.error, duration: 10 });
      } else if (result.dispatch?.jobId != null) {
        const jobId = result.dispatch.jobId;
        notification.success({
          message: `已保存为 v${result.sceneVersion}，并发起场景生成`,
          btn: <Button onClick={() => navigate(`/scene-generation/job-tasks/${jobId}`)}>查看生成任务</Button>,
          duration: 10,
        });
      } else {
        message.success(result.changed ? `场景信息和文件已保存为 v${result.sceneVersion}` : '内容未变化');
      }
    } catch (error) {
      const text = error instanceof Error ? error.message : '事件配置有误，请检查后重试';
      setSaveError(text);
      // The server may already have committed. Freeze the submitted payload so
      // retrying can recover its receipt without changing or dispatching it twice.
      setRetrySaveRequest(submitted && (!(error instanceof ApiBusinessError) || error.data?.retrySave === true)
        ? submitted : null);
      if (error instanceof ApiBusinessError && ['SCENE_BUSY', 'VERSION_CONFLICT', 'FILE_CONFLICT'].includes(String(error.data?.reason))) {
        setDraftNeedsReload(true);
      }
      const name = error instanceof ApiBusinessError ? error.data?.fileName : undefined;
      if (typeof name === 'string') {
        setActiveTab('files');
        const row = bosFiles.find(file => file.name === name);
        if (row) void openBosFile(row);
      }
    } finally {
      setSaving(false);
    }
  };

  const savedCount = items.filter(it => savedKeys.has(itemDraftKey(mode, it))).length;
  const canPrev = currentIdx > 0;
  const canNext = currentIdx < items.length - 1;

  // 右上 header 标签
  const headerTags: React.ReactNode[] = [];
  if (cur.source) {
    headerTags.push(
      <Tag key="src" color={cur.source === 'jira' ? 'blue' : 'cyan'}>{cur.source}</Tag>,
    );
  }
  if (cur.algorithmVersion) headerTags.push(<Tag key="alg">{cur.algorithmVersion}</Tag>);
  if (cur.existing) {
    headerTags.push(
      <Tag
        key="exist"
        color="orange"
        style={{ cursor: 'pointer' }}
        onClick={() => navigate(`/scene-annotate?sceneId=${cur.existing!.sceneId}`)}
      >
        已存在 #{cur.existing.sceneId}
      </Tag>,
    );
  }
  if (savedInLocal) headerTags.push(<Tag key="saved" color="green" icon={<CheckCircleOutlined />}>本地草稿</Tag>);
  if (dirty && !savedInLocal) headerTags.push(<Tag key="dirty" color="warning" icon={<EditOutlined />}>未保存改动</Tag>);
  // 版本 chip:scene mode 有 sceneMeta.sceneVersion 时展示,点击开版本历史抽屉。
  // 老场景 sceneVersion=0(PR-1 前) → 展示为 v0 提示"未版本化"。历史记录直接看下方
  // 「更新记录」卡,不再单独开抽屉。
  if (mode === 'scene' && sceneMeta?.sceneVersion != null) {
    const v = sceneMeta.sceneVersion;
    headerTags.push(
      <Tag
        key="ver"
        color={v > 0 ? ACCENT : 'default'}
      >
        <BranchesOutlined /> v{v}
      </Tag>,
    );
  }

  // "编辑上下文"是否可见:一期 scene mode 编辑区常显、mini 队列/上下条不再需要,
  // 仅保留供 code mode 分支(二期)使用;此处直接跟 mode === 'code' 联动即可。
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error mode 已被上面 early-return 收窄为 'scene',code 分支仅为二期占位保留
  const showEditingControls = mode === 'code' || savedInLocal;

  return (
    <div style={{ padding: '20px 24px' }}>
      {/* ══════════════════ 队列导航条 ══════════════════
         外壳常显 (返回按钮永远在);内部编辑相关部件按 showEditingControls 显隐。 */}
      <div style={{
        background: '#fff', border: '1px solid #f0f0f0', borderRadius: 8,
        padding: '10px 14px', marginBottom: 14,
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        <Button
          type="text" icon={<ArrowLeftOutlined />}
          onClick={() => {
            if (mode === 'scene') goBackToSource();
            else navigate('/scene-generation');
          }}
        >{mode === 'scene' ? '返回' : '退出标注'}</Button>

        {showEditingControls && (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {mode === 'scene' ? '编辑场景' : '标注进度'}:<b style={{ color: ACCENT }}>{currentIdx + 1}</b> / {items.length}
          {mode !== 'scene' && (
            <>
              <span style={{ margin: '0 6px', color: '#d9d9d9' }}>·</span>
              已保存 <b style={{ color: '#16a34a' }}>{savedCount}</b>
            </>
          )}
        </Text>
        )}

        {/* mini 队列 + 上下条 + 统一提交:批量 (code) mode 才需要;
            单场景 (scene) mode 一次只编辑一个场景,直接内嵌保存/取消,不需要队列导航。 */}
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-expect-error mode 已被上面 early-return 收窄为 'scene',code 分支仅为二期占位保留 */}
        {mode === 'code' && showEditingControls && (
          <div style={{
            display: 'flex', gap: 6, overflowX: 'auto', maxWidth: 640,
            padding: '2px 0', flex: '1 1 auto', minWidth: 240,
          }}>
            {items.map((it, i) => {
              const isCur = i === currentIdx;
              const isSaved = savedKeys.has(itemDraftKey(mode, it));
              return (
                <button
                  key={it.code}
                  type="button"
                  onClick={() => setCurrentIdx(i)}
                  style={{
                    border: `1px solid ${isCur ? ACCENT : '#f0f0f0'}`,
                    background: isCur ? `${ACCENT}12` : '#fafafa',
                    color: isCur ? ACCENT : '#333',
                    padding: '3px 10px', borderRadius: 4,
                    cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}
                >
                  <span style={{ fontFamily: MONO }}>{i + 1}</span>
                  <span style={{ fontFamily: MONO }}>{it.code}</span>
                  {isSaved
                    ? <CheckCircleOutlined style={{ color: '#16a34a', fontSize: 11 }} />
                    : !it.resolved
                      ? <ExclamationCircleOutlined style={{ color: '#dc2626', fontSize: 11 }} />
                      : null}
                </button>
              );
            })}
          </div>
        )}

        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-expect-error mode 已被上面 early-return 收窄为 'scene',code 分支仅为二期占位保留 */}
        {mode === 'code' && showEditingControls && (
          <Space size={6}>
            <Button
              size="small" icon={<LeftOutlined />} disabled={!canPrev}
              onClick={() => setCurrentIdx(currentIdx - 1)}
            >上一条</Button>
            <Button
              size="small" type="primary" icon={<RightOutlined />} disabled={!canNext}
              onClick={() => setCurrentIdx(currentIdx + 1)}
            >下一条</Button>
            <Tooltip title={
              savedCount === 0
                ? '至少保存 1 条草稿才能统一提交'
                : '后端 annotate-submit 接口待接入,当前点击只在 console 打印草稿'
            }>
              <Button
                size="small" icon={<ThunderboltOutlined />}
                onClick={() => void handleSubmitAll()}
                disabled={savedCount === 0}
              >
                统一提交 <Tag color={ACCENT} style={{ marginLeft: 4 }}>{savedCount}/{items.length}</Tag>
              </Button>
            </Tooltip>
          </Space>
        )}
      </div>

      {/* ══════════════════ 场景 header ══════════════════ */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        marginBottom: 12, flexWrap: 'wrap',
      }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            <span style={{ fontFamily: MONO, fontSize: 15, color: '#8c8c8c' }}>{cur.code}</span>
            {cur.existing && (
              <>
                <Text type="secondary" style={{ margin: '0 8px', fontWeight: 400 }}>·</Text>
                <span style={{ fontSize: 16, fontWeight: 500 }}>{cur.existing.sceneName || '(未命名)'}</span>
              </>
            )}
          </Title>
          <Space size={6} style={{ marginTop: 4 }}>{headerTags}</Space>
        </div>

        {/* 编辑/保存/取消:scene mode 显示,放在 header 右侧(mockup:场景缩略图上方)。
            editing=false 时是「重新构建 + 编辑」;进入 editing 后展开 保存 + 取消(dirty 时才启用保存)。 */}
        {mode === 'scene' && (
          <Space size={8} style={{ marginLeft: 'auto' }}>
            {!editing ? (
              <>
                {/* 「重新构建」：非终态（已录入 / 质检中）高亮 —— 这类场景还没走完生成 + 质检
                    流程，需要重新构建；其余状态不高亮但照常可点。构建在跑时不置灰（撞链路由后端
                    needConfirm 兜底，卡死的生成锁本来就靠重建恢复），只在派发在途时禁用防连点。 */}
                <Popconfirm
                  title="重新构建该场景？"
                  description={needsRebuild
                    ? `当前状态「${sceneMeta?.statusName || '非终态'}」还没走完生成 / 质检流程，需要重新构建：改用源数据重新生成整个场景目录，当前内容会被覆盖（旧版本可在「更新记录」里回退），任务结束前不能编辑此场景。`
                    : '改用源数据重新生成整个场景目录，当前内容会被覆盖（旧版本可在「更新记录」里回退），任务结束前不能编辑此场景。'}
                  okText="确认重新构建" cancelText="取消"
                  placement="bottomRight"
                  disabled={rebuilding || rebuildBlocked}
                  onConfirm={() => void handleRebuild()}
                >
                  <Button
                    icon={<BuildOutlined />}
                    type={needsRebuild ? 'primary' : 'default'}
                    danger={needsRebuild}
                    loading={rebuilding}
                    disabled={rebuilding || rebuildBlocked}
                  >重新构建</Button>
                </Popconfirm>
                <Button
                  type="primary" icon={<EditOutlined />}
                  loading={startingEdit}
                  disabled={rollingBackLogId != null || generationBusy || checkingGeneration || !!generationCheckError}
                  onClick={handleStartEdit}
                >编辑</Button>
                {isDerived && (
                  <Popconfirm
                    title="删除此派生场景"
                    description="将物理删除该场景的数据库记录与 BOS 文件,不可恢复。确定删除?"
                    okText="确定删除"
                    okButtonProps={{ danger: true }}
                    cancelText="取消"
                    onConfirm={handleDelete}
                  >
                    <Button danger icon={<DeleteOutlined />} loading={deleting} disabled={editBlocked}>删除此派生场景</Button>
                  </Popconfirm>
                )}
              </>
            ) : (
              <>
                {/* 另存为:复用编辑态 eventsForm 的现值 events,故放在编辑态内。 */}
                <Tooltip title={contentDraft.filesDirty ? '请先保存或取消文件修改，再另存为' : ''}>
                  <Button disabled={saving || editBlocked || contentDraft.filesDirty} onClick={() => setSaveAsOpen(true)}>另存为新场景</Button>
                </Tooltip>
                <Button
                  type="primary" icon={<SaveOutlined />}
                  loading={saving}
                  disabled={(!dirty && !hasTagChanges) || editBlocked || (contentDraft.editMode === 'files' && !!contentDraft.error)}
                  onClick={() => void handleSubmitAll()}
                >保存</Button>
                <Button
                  disabled={saving}
                  onClick={handleCancel}
                >取消</Button>
              </>
            )}
          </Space>
        )}

        {/* 批次级选项摘要:仅 code mode 显 (单场景走场景既有字段,不需要重复选)。
            code mode 已被上方 early-return,mode 收窄为 'scene',此处 ts 断言绕过。 */}
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-expect-error mode 已被上面 early-return 收窄为 'scene',code 分支仅为二期占位保留 */}
        {mode === 'code' && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <Tag color={batchSource === 'jira' ? 'blue' : 'cyan'}>
              批次来源:{batchSource === 'jira' ? 'Jira' : 'Case-Flow'}
            </Tag>
            {batchConfidential && (
              <Tag color="orange">🔒 保密</Tag>
            )}
            {batchSceneSetNodeId != null && (
              <Tag color="purple">场景集 #{batchSceneSetNodeId}</Tag>
            )}
          </div>
        )}
      </div>

      {/* preview 拉失败:整卡红条 + 允许下一条 */}
      {!cur.resolved && (
        <Alert
          type="error" showIcon
          message={`preview 未成功:${cur.error || '未知错误'}`}
          description="该条无法进入标注,提交时会被后端拒绝(annotate-submit 上线后);可以先跳到下一条继续。"
          style={{ marginBottom: 14 }}
        />
      )}

      {/* ══════════════════ 两栏:左(2/3)Tab + 右(1/3) 缩略图/基础信息 ══════════════════
          场景信息 Tab 内三区块(可编辑内容 / 高级参数 / 所属场景集),字段直接内嵌可改,
          底部一个「保存改动 & 取消」区,取代原独立编辑卡与「编辑」按钮。 */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1.85fr 1fr',
        gap: 16, marginBottom: 16,
        alignItems: 'stretch',
      }}>
        {/* ── 左列:场景信息 / 场景文件 Tabs ── */}
        <Card size="small" styles={{ body: { padding: 0 } }}>
          {saveError && <Alert type="error" showIcon message={saveError}
            description={retrySaveRequest ? '保存结果尚待确认。修改内容已保留，请重试原请求，系统会避免重复保存或生成。' : editing ? '本次修改仍保留在编辑器中。' : undefined}
            action={retrySaveRequest && <Button size="small" loading={saving}
              onClick={() => void handleSubmitAll(retrySaveRequest)}>重试保存</Button>} />}
          {sceneHealth?.abnormal && (() => {
            const taskFailed = sceneHealth.kind === 'task_failed';
            const disabled = !!sceneMeta?.isDeleted;
            const disabledMeta = (sceneMeta?.external as Record<string, unknown> | undefined)?.disabled as
              { trigger?: string } | undefined;
            return <Alert
              type="error" showIcon
              message={`当前场景异常：${sceneHealth.sceneStatusName}`}
              description={<Space direction="vertical" style={{ width: '100%' }} size={4}>
                <div>
                  <Typography.Text>
                    {sceneHealth.sceneInfo || (taskFailed ? '生成任务异常结束，未提供失败原因。' : '未提供失败原因。')}
                  </Typography.Text>
                </div>
                {taskFailed && sceneHealth.task && <Space wrap>
                  <Typography.Text strong>生成任务 #{sceneHealth.task.jobId}</Typography.Text>
                  <Tag color="error">{sceneHealth.task.statusName}</Tag>
                  <Button type="link" size="small"
                    onClick={() => navigate(`/scene-generation/job-tasks/${sceneHealth.task?.jobId}`)}>查看任务</Button>
                </Space>}
                {disabled && <Typography.Text type="secondary">
                  {disabledMeta?.trigger === 'regeneration_failed'
                    ? '该场景因重新生成失败已暂停使用，不会出现在任务里；重新生成成功、豁免或回退到历史版本后自动恢复。'
                    : '该场景已被禁用，不会出现在任务里；解除禁用后恢复。'}
                </Typography.Text>}
                {sceneHealth.rollbackBlocked && <Typography.Text type="secondary">
                  本次生成任务尚未确认结束，暂不能回退到历史版本；可点「重新生成」重跑一次，成功后自动恢复。
                </Typography.Text>}
                <Space wrap>
                  <Tooltip title={sceneHealth.rollbackBlocked ? '生成任务尚未确认结束，请等待' : ''}>
                    <Button size="small" icon={<HistoryOutlined />}
                      disabled={sceneHealth.rollbackBlocked}
                      onClick={handleGotoHistory}>回退到历史版本</Button>
                  </Tooltip>
                  {/* 逃生门：生成锁挡住回退时（任务崩了 / 提交未确认），只有重新生成能恢复。 */}
                  {sceneHealth.rollbackBlocked && <Button size="small" type="primary" ghost
                    icon={<ReloadOutlined />} loading={regenerating}
                    onClick={() => void handleRegenerate()}>重新生成</Button>}
                  {/* 豁免入口只看场景状态(9 不合格),不看 kind:最近一次生成任务硬失败时
                      kind 被判成 task_failed(质检剔除的场景常常同时是 failed 的生成任务),
                      但「能不能豁免」由 QC 结论决定——与「场景生成任务详情」面板同口径。 */}
                  {sceneHealth.sceneStatus === 9 && <Button size="small" danger
                    onClick={() => setWaiveOpen(true)}>豁免为瑕疵品</Button>}
                </Space>
              </Space>}
              style={{ marginBottom: 0 }}
            />;
          })()}
          {generationBusy && <Alert type="info" showIcon
            message="场景生成尚未完成，场景信息和文件暂为只读。"
            description={generationTasks.length > 0 ? <Space direction="vertical" style={{ width: '100%' }}>
              {generationTasks.map(task => <div key={task.taskId}>
                <Space wrap>
                  <Typography.Text strong>生成任务 #{task.jobId}</Typography.Text>
                  <Typography.Text>{task.jobName}</Typography.Text>
                  <Tag color={[3, 5, 10].includes(task.status) ? 'processing' : 'warning'}>{task.statusName}</Tag>
                  <Button type="link" size="small" onClick={() => navigate(`/scene-generation/job-tasks/${task.jobId}`)}>查看任务</Button>
                </Space>
                <div><Typography.Text type="secondary">子任务 #{task.taskId} · {task.createdAt} · {task.reason}</Typography.Text></div>
              </div>)}
            </Space> : '场景文件尚未生成或生成提交待确认，请先检查场景生成记录。'} />}
          {generationCheckError && <Alert type="warning" showIcon message="暂时无法确认生成任务状态，编辑和保存已暂停。" description={generationCheckError} />}
          {editing && draftNeedsReload && !retrySaveRequest && <Alert type="warning" showIcon
            message="编辑期间场景已开始生成或版本发生变化，本次草稿已保留，不能直接保存。"
            description="任务结束后，请先复制需要保留的内容，再加载最新版本重新编辑。"
            action={<Button size="small" disabled={generationBusy || checkingGeneration || !!generationCheckError || saving} loading={startingEdit}
              onClick={() => modal.confirm({
                title: '加载最新版本', content: '将丢弃本次草稿并加载最新场景，请确认已复制需要保留的内容。',
                okText: '丢弃草稿并加载', cancelText: '保留草稿', onOk: handleStartEdit,
              })}>加载最新版本</Button>} />}
          {editing && !editBlocked && <Alert type="info" showIcon message={contentDraft.editMode === 'fields'
            ? '本次已修改场景信息，场景文件暂为只读；撤回全部修改后可切换。'
            : contentDraft.editMode === 'files'
              ? '本次已修改场景文件，场景信息暂为只读；保存时会同步共有字段。撤回全部修改后可切换。'
              : '场景信息和场景文件不能同时修改。实际修改一侧后，另一侧会变为只读。'} />}
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            size="small"
            tabBarStyle={{ padding: '0 12px', marginBottom: 0 }}
            items={[
              {
                key: 'info',
                label: '场景信息',
                children: <ConfigProvider componentDisabled={fieldsReadOnly}><div style={{ padding: 16 }}>{renderSceneInfoTab()}</div></ConfigProvider>,
              },
              ...(mode === 'scene' ? [{
                key: 'files',
                label: '场景文件',
                children: <div style={{ padding: 12 }}>{renderSceneFilesTab()}</div>,
              }] : []),
            ]}
          />
        </Card>

        {/* ── 右列:上=缩略图,下=基础信息(只读) ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* 缩略图 Card;webp 拉不到 / 解码失败 → 留占位 */}
          <Card title="场景缩略图" size="small" extra={<Tag>preview.webp</Tag>}>
            {typeof cur.external?.previewUrl === 'string' && cur.external.previewUrl && !previewBroken ? (
              <div style={{ textAlign: 'center' }}>
                <WebpPlayer
                  src={cur.external.previewUrl}
                  alt={cur.existing?.sceneName || cur.code}
                  maxHeight={220}
                  hideOnError
                  onError={() => setPreviewBroken(true)}
                />
              </div>
            ) : (
              <div style={{ minHeight: 40 }} />
            )}
          </Card>

          {/* 基础信息 Card:只读标识字段,与左侧「可编辑内容」互补,不放可编辑项。
              scene mode 才有完整元数据;code mode 的 preview 视角展示走 Tab 内的兜底分支。 */}
          <Card title={<Space><InfoCircleOutlined /><span>基础信息</span></Space>} size="small" style={{ flex: 1 }}>
            {mode === 'scene' && sceneMeta ? renderBasicInfoPanel() : (
              <Text type="secondary" style={{ fontSize: 12 }}>基础信息在单场景视图可见</Text>
            )}
          </Card>
        </div>
      </div>

      {/* ══════════════════ 底部更新记录 · 仅 scene mode ══════════════════ */}
      {mode === 'scene' && (
        <div ref={historyCardRef}>
          <Card
            title={<Space><HistoryOutlined /><span>更新记录</span></Space>}
            size="small"
            style={{
              marginTop: 16,
              boxShadow: historyHighlight ? `0 0 0 2px ${ACCENT}` : undefined,
              transition: 'box-shadow .3s ease',
            }}
          >
            {renderHistoryPanel()}
          </Card>
        </div>
      )}

      {renderWaiveModal()}

      {/* 另存为新场景弹窗:events 复用编辑态 eventsForm(single source of truth) */}
      <SaveAsModal
        open={saveAsOpen}
        baseSceneName={sceneMeta?.name}
        baseDescription={sceneMeta?.description}
        eventsCount={watchedEvents?.length ?? 0}
        submitting={saveAsSubmitting}
        onCancel={() => setSaveAsOpen(false)}
        onSubmit={handleSaveAs}
      />
    </div>
  );

  // ─────────────────────────────────────────────
  // 右侧 Tabs 子渲染
  // ─────────────────────────────────────────────

  /** Tab1 场景信息:scene mode 展开三区块(可编辑内容 / 高级参数 / 所属场景集)。
   *  默认只读展示(MetaItem 无边框);点右上「编辑」进入 editing=true,
   *  字段换成 FieldRow + Input 可写。保存/取消都在页面右上,不在 Tab 内部。
   *  code mode 兜底展示 preview 视角的元信息(二期开放批量入口时再复用)。 */
  function renderSceneInfoTab() {
    if (mode === 'scene' && sceneMeta) {
      // 只读态直接读 sceneMeta / preview draft;editing 态读 draft(setField 已即时 setDrafts)。
      // 展示时间统一走 formatDisplayTime,输入态直接原样,保存前 back-fill。
      const sst = editing ? (draft.sceneStartTime ?? null) : formatDisplayTime(effectiveFields?.sceneStartTime ?? sceneMeta.sceneStartTime ?? null);
      const set = editing ? (draft.sceneEndTime ?? null) : formatDisplayTime(effectiveFields?.sceneEndTime ?? sceneMeta.sceneEndTime ?? null);
      const itime = editing ? (draft.issueTime ?? null) : formatDisplayTime(effectiveFields ? effectiveFields.issueTime : draft.issueTime);
      const openSw = editing
        ? (draft.openSwitchClosedLoopSec ?? null)
        : (effectiveFields ? effectiveFields.openSwitchClosedLoopSec ?? null : draft.openSwitchClosedLoopSec ?? orig?.openSwitchClosedLoopSec ?? null);
      const maskFinish = editing
        ? (draft.maskFinishSignal ?? false)
        : (effectiveFields ? effectiveFields.maskFinishSignal ?? false : draft.maskFinishSignal ?? orig?.maskFinishSignal ?? false);
      const cloudPolicy: CloudCmdPolicy = editing
        ? (draft.cloudCmdPolicy ?? 'passthrough')
        : (effectiveFields ? effectiveFields.cloudCmdPolicy ?? 'passthrough' : draft.cloudCmdPolicy ?? orig?.cloudCmdPolicy ?? 'passthrough');
      const cloudPolicyLabel = (v: CloudCmdPolicy) => CLOUD_CMD_POLICY_OPTIONS.find(o => o.value === v)?.label ?? v;
      return (
        <div>
          {/* ── 区块 1 · 场景参数 ── */}
          <SectionHeader title="场景参数" />
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
            columnGap: 20, rowGap: 0,
          }}>
            {editing ? (
              <>
                <FieldRow
                  label="场景开始时间" required
                  origHint={draft.sceneStartTime !== orig?.sceneStartTime ? (orig?.sceneStartTime ?? '空') : undefined}
                >
                  <Input
                    value={draft.sceneStartTime ?? ''}
                    placeholder="2026-06-12 14:30:15.000"
                    onChange={e => setField('sceneStartTime', e.target.value || null)}
                  />
                </FieldRow>
                <FieldRow
                  label="场景结束时间" required
                  origHint={draft.sceneEndTime !== orig?.sceneEndTime ? (orig?.sceneEndTime ?? '空') : undefined}
                >
                  <Input
                    value={draft.sceneEndTime ?? ''}
                    placeholder="2026-06-12 14:31:03.200"
                    onChange={e => setField('sceneEndTime', e.target.value || null)}
                  />
                </FieldRow>
                {/* 故障时刻:可编辑。数据源在 external.jira.issue_time,后端 batch_update
                    白名单支持写回;命中改动会把场景锁为 MANUAL,不再被 Jira 自动刷新覆盖。 */}
                <FieldRow
                  label="故障时刻" hint="绝对时间;清空 = 移除"
                  origHint={draft.issueTime !== orig?.issueTime ? (orig?.issueTime ?? '空') : undefined}
                >
                  <Input
                    value={draft.issueTime ?? ''}
                    placeholder="2026-06-12 14:30:51.000"
                    allowClear
                    onChange={e => setField('issueTime', e.target.value || null)}
                  />
                </FieldRow>
                <FieldRow
                  label="场景标签" hint="下拉选历史标签,或直接输入新标签;清空 = 移除"
                  origHint={draft.label !== orig?.label ? (orig?.label ?? '空') : undefined}
                >
                  <AutoComplete
                    value={draft.label ?? ''}
                    options={labelOptions.map(v => ({ value: v }))}
                    placeholder="例:绕行 / 变道 / 无保护左转"
                    allowClear
                    // 前端做子串模糊 (case-insensitive);后端下发的已经去重去空。
                    filterOption={(input, option) =>
                      (option?.value ?? '').toString().toLowerCase().includes(input.toLowerCase())
                    }
                    // 语义:后端 SceneFieldPatch.label 「None=不改, ''=清空, 非空=更新」。
                    // - 用户清空(x 按钮或全删) → 传 '' 让保存链路命中"清空 DB 置 NULL"分支
                    // - 有值输入 / 选中 → 传字符串,后端会 trim 后再比对
                    onChange={v => setField('label', typeof v === 'string' ? v : '')}
                    onClear={() => setField('label', '')}
                    style={{ width: '100%' }}
                  />
                </FieldRow>
                <FieldRow
                  label="地图名称"
                  origHint={draft.mapName !== orig?.mapName ? (orig?.mapName ?? '空') : undefined}
                >
                  <Input
                    value={draft.mapName ?? ''} placeholder="shunyi_hd_map"
                    allowClear
                    onChange={e => setField('mapName', e.target.value || null)}
                  />
                </FieldRow>
                <FieldRow
                  label="地图版本"
                  origHint={draft.mapVersion !== orig?.mapVersion ? (orig?.mapVersion ?? '空') : undefined}
                >
                  <Input
                    value={draft.mapVersion ?? ''} placeholder="v2.4.1"
                    allowClear
                    onChange={e => setField('mapVersion', e.target.value || null)}
                  />
                </FieldRow>
              </>
            ) : (
              <>
                <MetaItem label="场景开始时间" value={sst} />
                <MetaItem label="场景结束时间" value={set} />
                <MetaItem label="故障时刻" value={itime} />
                <MetaItem label="场景标签" mono={false} value={effectiveFields ? effectiveFields.label : sceneMeta.label ?? '-'} />
                <MetaItem label="地图名称" value={effectiveFields ? effectiveFields.mapName : sceneMeta.mapName} />
                <MetaItem label="地图版本" value={effectiveFields ? effectiveFields.mapVersion : sceneMeta.mapVersion} />
              </>
            )}
          </div>
          {/* ── 标签树根节点映射 ──
             每个根节点一个可编辑位置,一行 3 个自动折行(当前 11 个根 = 4 行)。编辑态
             = TreeSelect 单选该根下一个叶子,支持按中文名搜索;只读态 = 已挂叶子 chip,
             悬浮看英文键/别名/创建信息。与「标签管理」页共用同一棵树,caseflow 同步的
             新根自动出现在这里。 */}
          {mode === 'scene' && rootTags.length > 0 && (
            <div style={{ marginBottom: 4 }}>
              <SectionHeader title="标签集" style={{ marginTop: 10 }} />
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
                columnGap: 16, rowGap: 10,
              }}>
                {rootTags.map((root) => {
                  const leaves = collectLeafTags(root);
                  const assigned = assignedTagOfRoot(root);
                  return (
                    <div key={root.id} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <span style={{
                        flexShrink: 0, maxWidth: 128, overflow: 'hidden', textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap', fontSize: 12, color: '#6b7280',
                      }} title={tagDisplayName(root)}>
                        {tagDisplayName(root)}标签
                      </span>
                      {editing ? (
                        <TreeSelect
                          size="small"
                          style={{ flex: 1, minWidth: 0 }}
                          allowClear
                          showSearch
                          treeNodeFilterProp="title"
                          // 下拉不跟随格子宽度(格子只有 ~150px):按内容自适应,
                          // 深层目录引出的长标签名才能完整显示;该模式 rc-tree-select
                          // 会关虚拟滚动改为全量渲染,单棵最多数百节点可接受。
                          popupMatchSelectWidth={false}
                          placeholder={leaves.length ? '选择标签' : '暂无标签'}
                          disabled={leaves.length === 0}
                          treeData={rootTreeDataMap.get(root.id)}
                          value={assigned ? assigned.id : undefined}
                          onChange={(v) => void setRootTag(root, v as number | undefined)}
                          treeDefaultExpandAll
                        />
                      ) : assigned ? (
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <Tooltip title={tagInfoTitle(assigned)}>
                            <Tag style={{
                              marginInlineEnd: 0, display: 'block', maxWidth: '100%',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>{tagDisplayName(assigned)}</Tag>
                          </Tooltip>
                        </span>
                      ) : (
                        <span style={{ color: '#c7c7cf' }}>-</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* 场景描述单独一行(多行 textarea/纯文本,占满宽度) */}
          {editing ? (
            <FieldRow
              label="场景描述" hint="人工填写的场景说明,清空 = 移除"
              origHint={draft.description !== orig?.description ? (orig?.description ?? '空') : undefined}
            >
              <Input.TextArea
                value={draft.description ?? ''}
                placeholder="补充说明这个场景的关键情境 / 复现路径 / 期望表现"
                autoSize={{ minRows: 2, maxRows: 6 }}
                maxLength={4000}
                showCount
                allowClear
                onChange={e => setField('description', e.target.value)}
              />
            </FieldRow>
          ) : (
            <MetaItem label="场景描述" value={effectiveFields ? effectiveFields.description : sceneMeta.description ?? null} mono={false} />
          )}

          {/* 地图缺失软警告(来自 preview) */}
          {cur.mapMissingReason && (
            <Alert
              type="warning" showIcon
              message="地图缺失软警告"
              description={cur.mapMissingReason}
              style={{ marginBottom: 14 }}
            />
          )}

          {/* ── 区块 2 · 高级参数 ── */}
          <SectionHeader title="高级参数" style={{ marginTop: 8 }} />
          {editing ? (
            <FieldRow
              label="开环切闭环秒数" hint="秒"
              description="以仿真开始时间为起点,偏移多少秒作为算法控车时间点;负数统一按 -1(开环仿真)处理;默认 0。"
              origHint={
                (draft.openSwitchClosedLoopSec ?? null) !== (orig?.openSwitchClosedLoopSec ?? null)
                  ? (orig?.openSwitchClosedLoopSec ?? '空').toString() : undefined
              }
            >
              <InputNumber
                value={draft.openSwitchClosedLoopSec}
                step={0.01} style={{ width: 220 }}
                onChange={v => setField('openSwitchClosedLoopSec', v as number | null)}
              />
            </FieldRow>
          ) : (
            <MetaItem
              label="开环切闭环秒数"
              value={openSw == null ? null : `${openSw} s`}
            />
          )}
          {editing ? (
            <FieldRow
              label="是否屏蔽 openAPI FINISH 信号"
              description="开启后仿真侧屏蔽 openAPI FINISH 信号（走额外逻辑）;默认关闭（正常透传）。"
              origHint={
                (draft.maskFinishSignal ?? false) !== (orig?.maskFinishSignal ?? false)
                  ? (orig?.maskFinishSignal ? '是' : '否') : undefined
              }
            >
              <Switch
                checked={maskFinish}
                checkedChildren="是" unCheckedChildren="否"
                onChange={v => setField('maskFinishSignal', v)}
              />
            </FieldRow>
          ) : (
            <MetaItem
              label="是否屏蔽 openAPI FINISH 信号"
              value={maskFinish ? '是' : '否'}
            />
          )}

          {editing ? (
            <FieldRow
              label="云控指令合并策略"
              description={CLOUD_CMD_POLICY_HINT}
              origHint={
                (draft.cloudCmdPolicy ?? 'passthrough') !== (orig?.cloudCmdPolicy ?? 'passthrough')
                  ? cloudPolicyLabel(orig?.cloudCmdPolicy ?? 'passthrough') : undefined
              }
            >
              <Select
                style={{ width: 160 }}
                value={cloudPolicy}
                options={CLOUD_CMD_POLICY_OPTIONS}
                onChange={v => setField('cloudCmdPolicy', v)}
              />
            </FieldRow>
          ) : (
            <MetaItem label="云控指令合并策略" value={cloudPolicyLabel(cloudPolicy)} />
          )}

          {/* ── 区块 · 云端控制事件 ── */}
          {renderEventsSection()}

          {/* ── 区块 3 · 所属场景集(只读 chips) ── */}
          <SectionHeader title="所属场景集" style={{ marginTop: 8 }} />
          {sceneSetsLoading ? (
            <Spin size="small" />
          ) : sceneSets.length === 0 ? (
            <Text type="secondary" style={{ fontSize: 12 }}>该场景未加入任何场景集</Text>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {sceneSets.map(s => (
                <div
                  key={s.treeNodeId}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '4px 8px', borderRadius: 4,
                    background: '#fafafa', border: '1px solid #f0f0f0',
                    fontSize: 12,
                  }}
                >
                  <Tag color="purple" style={{ margin: 0 }}>#{s.treeNodeId}</Tag>
                  <span style={{ color: '#595959' }}>{s.path}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }
    // code mode:preview 只吐了这几个字段,展示 preview 视角的元信息(二期恢复批量入口时用)
    return (
      <div>
        <MetaItem label="CODE" value={cur.code} />
        <MetaItem label="SOURCE" value={cur.source} />
        <MetaItem label="ALGORITHM_VERSION" value={cur.algorithmVersion ?? null} />
        {cur.existing ? (
          <>
            <MetaItem label="EXISTING_SCENE_ID" value={cur.existing.sceneId} />
            <MetaItem label="EXISTING_SCENE_NAME" value={cur.existing.sceneName} />
            <div style={{ marginTop: 8 }}>
              <Button
                size="small"
                type="link"
                onClick={() => navigate(`/scene-annotate?sceneId=${cur.existing!.sceneId}`)}
              >
                打开已存在场景 #{cur.existing.sceneId} →
              </Button>
            </div>
          </>
        ) : (
          <MetaItem label="EXISTING" value={null} />
        )}

        {/* 原始外源快照(可选;非空且不只是缩略图 URL 才显) */}
        {cur.external && Object.keys(cur.external).filter(k => k !== 'previewUrl').length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{
              fontSize: 11, color: '#9b9da3', textTransform: 'uppercase',
              letterSpacing: 0.5, marginBottom: 4,
            }}>ORIGINAL FIELDS</div>
            <pre style={{
              margin: 0, background: '#fafafa', borderRadius: 4, padding: 10,
              fontFamily: MONO, fontSize: 11, lineHeight: 1.55,
              maxHeight: 260, overflow: 'auto',
            }}>{JSON.stringify(
              Object.fromEntries(Object.entries(cur.external).filter(([k]) => k !== 'previewUrl')),
              null,
              2,
            )}</pre>
          </div>
        )}
      </div>
    );
  }

  /** 右下「基础信息」卡:只读标识字段,与左侧「可编辑内容」互补;仅 scene mode。
   *  两列布局(mockup 要求);时间字段走 formatDisplayTime,无 T,3 位毫秒。 */
  function renderBasicInfoPanel() {
    if (!sceneMeta) return null;
    const statusText = sceneMeta.statusName
      || (sceneMeta.status != null ? `#${sceneMeta.status}` : null);
    return (
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
        columnGap: 16, rowGap: 0,
      }}>
        <MetaItem label="SCENE_ID" value={sceneMeta.id} />
        <MetaItem label="API_CODE" value={sceneMeta.apiCode ?? null} />
        <MetaItem label="场景名称" value={sceneMeta.name} mono={false} />
        <MetaItem label="状态" value={
          statusText
            ? <Tag color={sceneMeta.status === 5 ? 'blue' : 'default'} style={{ margin: 0 }}>{statusText}</Tag>
            : null
        } mono={false} />
        <MetaItem label="来源" value={sceneMeta.source ?? null} mono={false} />
        <MetaItem label="场景版本" value={`v${sceneMeta.sceneVersion ?? '?'}`} />
        <MetaItem label="场景类型" value={sceneMeta.sceneType ?? null} mono={false} />
        <MetaItem label="算法版本" value={sceneMeta.algorithmVersion ?? null} />
        <MetaItem label="创建人" value={sceneMeta.creator} mono={false} />
        <MetaItem label="创建时间" value={formatDisplayTime(sceneMeta.createTime)} />
        <MetaItem label="更新时间" value={formatDisplayTime(sceneMeta.updateTime ?? null)} />
      </div>
    );
  }

  /** 云端控制事件 section:与 SceneBosFiles 编辑面板同款视觉。
   *  editing → 「云端控制事件 (cloud_control events)」标题 + 描述 + <Form> + EventListEditor;
   *  只读 → 简短列表(触发类型 / 模式 / 云控指令)。老场景 events=null 只读态渲染"未配置"。 */
  function renderEventsSection() {
    const evList = !editing && effectiveFields ? effectiveFields.events ?? [] : draft.events ?? [];
    return (
      <>
        <SectionHeader title="云端控制事件" style={{ marginTop: 8 }} />
        {editing ? (
          <div>
            <div style={{ marginBottom: 8 }}>
              <Text strong style={{ color: ACCENT }}>
                云端控制事件 (cloud_control events)
              </Text>
              <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)', marginTop: 2 }}>
                配置触发条件与绕行动作;不添加则为普通场景。删空 = 清空 external.events。
              </div>
            </div>
            <Form form={eventsForm} layout="vertical" disabled={fieldsReadOnly}
              onValuesChange={(_, values) => {
                if (fieldsReadOnly) return;
                const events = buildSubmitEvents(values.events ?? [], 'empty') ?? [];
                const fields = fieldPatch({ ...draft, events }, normalizedOriginal);
                setDrafts(prev => ({ ...prev, [cur.code]: { ...draft, events } }));
                contentDraft.syncFields({ events }, Object.keys(fields).length > 0);
              }}>
              <EventListEditor form={eventsForm} />
            </Form>
          </div>
        ) : evList.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 12 }}>未配置云端控制事件</Text>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {evList.map((ev, idx) => {
              const trig = ev.trigger.type === 'time'
                ? `time · ${ev.trigger.time ?? '-'}s`
                : `pos · (${ev.trigger.position?.x ?? '-'}, ${ev.trigger.position?.y ?? '-'}) r=${ev.trigger.radius ?? '-'}`;
              return (
                <div
                  key={idx}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '4px 8px', borderRadius: 4,
                    background: '#fafafa', border: '1px solid #f0f0f0',
                    fontSize: 12,
                  }}
                >
                  <Tag color={ev.mode === 'once' ? 'blue' : 'green'} style={{ margin: 0 }}>
                    {ev.mode}
                  </Tag>
                  <span style={{ color: '#595959', fontFamily: MONO }}>{trig}</span>
                  <span style={{ marginLeft: 'auto', color: '#1d1d1f', fontFamily: MONO }}>
                    {ev.action?.cloudCmd ?? '-'}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  }

  /** Tab2 场景文件:仅 scene mode;左列表 + 右内容,布局与原 SceneDetail 一致但更紧凑。 */
  function renderSceneFilesTab() {
    const selected = bosFiles.find(file => file.key === bosSelectedKey);
    const activeFile = selected ? contentDraft.files[selected.name] : undefined;
    return (
      <>
        {bosPrefix && (
          <div style={{ marginBottom: 8 }}>
            <Text code copyable={{ text: bosPrefix }} style={{ fontSize: 11 }}>
              {bosPrefix}
            </Text>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 12 }}>
          <div style={{
            maxHeight: 340, overflow: 'auto',
            border: '1px solid #f0f0f0', borderRadius: 6, padding: 6,
          }}>
            {bosLoading ? (
              <div style={{ textAlign: 'center', padding: 20 }}><Spin /></div>
            ) : bosFiles.length === 0 ? (
              <Empty description="该目录下暂无文件" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <BosFileList
                files={bosFiles}
                selectedKey={bosSelectedKey}
                onSelect={openBosFile}
                modifiedNames={new Set(Object.values(contentDraft.files).filter(file => file.content !== file.originalContent).map(file => file.name))}
              />
            )}
          </div>

          <div style={{
            border: '1px solid #f0f0f0', borderRadius: 6, padding: 10,
            minHeight: 220, maxHeight: editing ? 700 : 340, overflow: 'auto',
            background: '#fafafa',
          }}>
            {bosContentLoading ? (
              <div style={{ textAlign: 'center', padding: 30 }}><Spin /></div>
            ) : editing && activeFile ? (
              <SceneFileEditor file={activeFile} readOnly={filesReadOnly} onSave={() => void handleSubmitAll()}
                onChange={content => {
                  if (filesReadOnly) return;
                  const patch = contentDraft.change(activeFile.name, content);
                  if (!patch) return;
                  setDrafts(prev => ({ ...prev, [cur.code]: { ...(prev[cur.code] ?? draft), ...patch } }));
                  if ('events' in patch) eventsForm.setFieldsValue({ events: structuredClone(patch.events ?? []) });
                }} />
            ) : bosFileContent === null ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="点击左侧文件查看内容"
                style={{ paddingTop: 30 }}
              />
            ) : bosFileContent.viewType === 'image' && bosFileContent.previewUrl ? (
              <div style={{ textAlign: 'center' }}>
                <Image
                  src={bosFileContent.previewUrl}
                  alt={bosFileContent.name}
                  style={{ maxHeight: 300, objectFit: 'contain' }}
                  preview={{ mask: <span style={{ fontSize: 12 }}>查看大图</span> }}
                />
              </div>
            ) : (
              <pre style={{
                margin: 0, padding: 8, background: '#fff', borderRadius: 4,
                fontFamily: MONO, fontSize: 11, lineHeight: 1.55,
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>
                {bosFileContent.content}
              </pre>
            )}
          </div>
        </div>
      </>
    );
  }

  /** 豁免确认弹窗:与「场景生成任务详情」面板同交互（原因可选,≤500）。 */
  function renderWaiveModal() {
    return (
      <Modal
        title="豁免为瑕疵品"
        open={waiveOpen}
        onCancel={() => setWaiveOpen(false)}
        onOk={() => void handleWaive()}
        okText="确认豁免"
        cancelText="取消"
        okButtonProps={{ danger: true, loading: waiving }}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          状态将从「不合格」置为「已完成-瑕疵品」；因重新生成失败造成的禁用会一并解除。
          若该 Job 已关联场景集，会自动入集。
        </Typography.Paragraph>
        <Input.TextArea
          rows={3}
          maxLength={500}
          showCount
          placeholder="豁免原因（可选）"
          value={waiveReason}
          onChange={(e) => setWaiveReason(e.target.value)}
        />
      </Modal>
    );
  }

  /** 更新记录 = 场景版本历史:每条头行(v{N} + 来源 tag + 当前/未快照标 + 回退按钮)、
   *  副行(创建人 · 时间)、diff 列表。数据源 = GET /scenes/:id/versions,含老 log
   *  兜底(versionNo=null 显示"v?"、来源=edit 兜底)。分页 pageSize=5。 */
  function renderHistoryPanel() {
    if (historyLoading) {
      return <div style={{ textAlign: 'center', padding: 20 }}><Spin /></div>;
    }
    if (historyLogs.length === 0 && historyTotal === 0) {
      return <div style={{ minHeight: 40 }} />;
    }
    const sourceLabel: Record<string, string> = {
      initial: '初始',
      edit: '编辑',
      rollback: '回退',
      manual_upload: '脚本上传',
      generate: '首次生成',
      rebuild: '重新构建',
      time_rerun: '时间重跑',
    };
    const sourceColor: Record<string, string> = {
      initial: 'green',
      edit: 'default',
      rollback: 'orange',
      manual_upload: 'cyan',
      generate: 'blue',
      rebuild: 'geekblue',
      time_rerun: 'purple',
    };
    const borderColor: Record<string, string> = {
      initial: '#16a34a',
      edit: ACCENT,
      rollback: '#d97706',
      manual_upload: '#0891b2',
      generate: '#2563eb',
      rebuild: '#4f46e5',
      time_rerun: '#9333ea',
    };
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {historyLogs.map((v) => {
          const entries = Object.entries(v.changedFields || {}).filter(
            ([key]) => key !== 'files' && key !== 'update_time',
          );
          const fileNames = Object.keys(v.sourceRef?.changedFiles ?? {});
          const who = v.creator || '未知';
          const src = v.source || 'edit';
          const requiresRegeneration = v.sourceRef?.requiresRegeneration === true;
          const rerun: SceneTimeRerun | null | undefined = v.sourceRef?.timeRerun;
          const rerunStatus = rerun?.status ?? null;
          // 这一版对应的生成任务:重跑链路用 timeRerun.jobId,构建/生成占位行用 source_ref.jobId。
          const genJobId = rerun?.jobId ?? v.sourceRef?.jobId ?? null;
          // 引擎回传的原因:优先重跑链路那条(timeRerun.reason),其余链路(首建/重构建/
          // QC 定稿)记在 source_ref.resolveReasons 流水里。纯审计串(`scene_qc:pass
          // (mileage=…)`,code 为空且无 detail)对用户表达不了「时间为什么变」,不展示。
          const engineReason = pickEngineReason(rerun?.reason, v.sourceRef?.resolveReasons);
          // 老数据:一期的 time_rerun 占位行(changedFields 空,字段改动在上一条 edit 里)
          const legacyRerun = src === 'time_rerun' && entries.length === 0 && !rerun;
          const canRollback = !editing && !saving && !editBlocked && !v.isCurrent && !!v.versionBosKey && !requiresRegeneration;
          const regenerationTip = rerunStatus === 'running'
            ? '重新生成中，完成后可回退'
            : rerunStatus === 'superseded'
              ? '该次时间修改已被后续构建取代，请选择其它版本'
              : '该次时间修改未完成重新生成，请选择其它版本';
          return (
            <div
              key={v.id}
              style={{
                border: '1px solid #f0f0f0',
                borderLeft: `3px solid ${borderColor[src] || ACCENT}`,
                borderRadius: 6,
                padding: '10px 14px',
                background: '#fff',
              }}
            >
              {/* 头行:v{N} · 来源 tag · 当前/未快照 · 回退按钮(右) */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
              }}>
                <span style={{
                  fontWeight: 600, fontSize: 14, color: '#1d1d1f',
                  fontFamily: MONO,
                }}>v{v.versionNo ?? '?'}</span>
                {/* 来源 tag:edit 是编辑常态,不显以减噪;initial(初始)/rollback(回退)展示 */}
                {src !== 'edit' && (
                  <Tag color={sourceColor[src] || 'default'} style={{ margin: 0 }}>
                    {sourceLabel[src] || src}
                  </Tag>
                )}
                {rerunStatus && (
                  <Tag color={TIME_RERUN_STATUS_COLOR[rerunStatus] || 'default'} style={{ margin: 0 }}>
                    {TIME_RERUN_STATUS_LABEL[rerunStatus] || rerunStatus}
                  </Tag>
                )}
                {v.isCurrent && (
                  <Tag color="blue" style={{ margin: 0 }}>当前</Tag>
                )}
                {!v.versionBosKey && (
                  <Tooltip title="老历史 log 未生成 BOS 快照,不可回退到此">
                    <Tag style={{ margin: 0 }}>未快照</Tag>
                  </Tooltip>
                )}
                <div style={{ flex: 1 }} />
                {requiresRegeneration && (
                  <Tooltip title={regenerationTip}>
                    <Button size="small" type="link" style={{ padding: 0 }} disabled>回退到此</Button>
                  </Tooltip>
                )}
                {canRollback && (
                  <Popconfirm
                    title={`回退到 v${v.versionNo ?? '?'}?`}
                    description="不删后续版本,会用旧版内容新建一个新版本;时间/地图/标签/描述/开环秒/故障时刻/云控事件都会拉回旧值。"
                    okText="确认回退"
                    cancelText="取消"
                    onConfirm={() => handleRollback(v)}
                    disabled={rollingBackLogId != null || generationBusy || checkingGeneration || !!generationCheckError}
                  >
                    <Button
                      size="small"
                      type="link"
                      style={{ padding: 0 }}
                      loading={rollingBackLogId === v.logId}
                      disabled={rollingBackLogId != null && rollingBackLogId !== v.logId}
                    >回退到此</Button>
                  </Popconfirm>
                )}
              </div>
              {/* 副行:头像 + 创建人 · 时间(靠右) */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, marginBottom: entries.length > 0 ? 8 : 0,
              }}>
                <span style={{
                  width: 22, height: 22, borderRadius: '50%',
                  background: `${ACCENT}1a`, color: ACCENT,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 600,
                }}>{who.slice(0, 1).toUpperCase()}</span>
                <Text strong style={{ fontSize: 13 }}>{who}</Text>
                {/* 这一版对应的生成任务:重跑链路记在 timeRerun.jobId,构建占位行记在
                    source_ref.jobId。没有任务的行(纯编辑 / 回退 / 老记录)不展示。 */}
                {genJobId != null && (
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, fontSize: 12, height: 'auto' }}
                    onClick={() => navigate(`/scene-generation/job-tasks/${genJobId}`)}
                  >生成任务 #{genJobId}</Button>
                )}
                <Text type="secondary" style={{ marginLeft: 'auto', fontSize: 12 }}>
                  {v.changeTime || ''}
                </Text>
              </div>
              {/* rollback 来源引用 */}
              {src === 'rollback' && v.sourceRef && (
                <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: entries.length > 0 ? 6 : 0 }}>
                  ← 来自 v{v.sourceRef.fromVersionNo ?? '?'}(log #{v.sourceRef.fromLogId ?? '?'})
                </div>
              )}
              {/* 时间重跑链路:引擎回执 + 重跑后状态（R1）。
                  引擎原因只认「已知分支码」映射出来的中文（``reason.code`` 非空）；
                  ``scene_qc:pass (mileage=…)`` 这类引擎内部审计串（code 为空）不展示 ——
                  它回答的是「引擎自己跑到哪一步」，对用户表达不了什么（2026-10-19 口径）。
                  「被引擎还原」也不再单独说明:下面的三段轨道自带结论。 */}
              {rerun?.status === 'running' && (
                <div style={{ marginBottom: 8, fontSize: 12 }}>
                  <Text type="secondary">重跑中，引擎结论待回传</Text>
                </div>
              )}
              {rerun?.sceneStatus === 'INVALIDATED' && (
                <div style={{ marginBottom: 8, fontSize: 12 }}>
                  <Tag color="error" style={{ margin: 0 }}>重跑后不合格</Tag>
                  {rerun.sceneInfo ? <Text type="secondary"> {rerun.sceneInfo}</Text> : null}
                </div>
              )}
              {fileNames.length > 0 && <div style={{ marginBottom: 8 }}>
                <Text type="secondary">修改文件：</Text>
                {fileNames.map(name => <Tag key={name}>{name}</Tag>)}
              </div>}
              {/* diff 列表 */}
              {entries.length === 0 ? (
                src === 'initial' ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>场景创建</Text>
                ) : legacyRerun ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    重新生成（旧记录，字段改动见上一条编辑）
                  </Text>
                ) : src === 'rollback' || fileNames.length > 0 ? null : (
                  <Text type="secondary" style={{ fontSize: 12 }}>无字段变化</Text>
                )
              ) : (
                <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {entries.flatMap(([field, diff]) => {
                    const view = versionDiffView(diff);
                    // `external` / `params` 这类结构化字段:整体渲染只能得到
                    // 「（结构化变更）→（结构化变更）」,看不出改了哪个键（改「开环转闭环
                    // 秒数」是这样,改「故障时刻」(external.jira.issue_time) 更是只到
                    // 「jira」一层）→ 展开到叶子,每个叶子**按场景信息面板的字段名**单独成行。
                    const subRows = objectDiffRows(view.before, view.requested, view.after);
                    const rows = subRows.length > 0
                      ? subRows.map(row => ({
                        key: `${field}.${row.path.join('.')}`,
                        label: CHANGE_LOG_FIELD_LABELS[row.key] || row.path.join('.'),
                        view: versionDiffView({
                          before: row.before,
                          requested: row.requested,
                          after: row.after,
                          engineOnly: diff.engineOnly === true,
                        }),
                      }))
                      : [{
                        key: field,
                        label: CHANGE_LOG_FIELD_LABELS[field] || field,
                        view,
                      }];
                    return rows.map(row => (
                      <div
                        key={row.key}
                        style={{
                          display: 'flex', alignItems: 'flex-end', gap: 10,
                          flexWrap: 'wrap', fontSize: 12,
                        }}
                      >
                        <span style={{
                          flex: '0 0 auto', minWidth: 88, color: '#595959', fontWeight: 500,
                          lineHeight: '15px',
                        }}>{row.label}</span>
                        {renderDiffTrack(row.view)}
                      </div>
                    ));
                  })}
                </div>
                {/* 引擎调整原因:只认已知分支码映射出来的中文（code 非空）。引擎内部审计串
                    不展示,「未回传」也不摆兜底文案（2026-10-19 口径）。 */}
                {entries.some(([, d]) => versionDiffView(d).engineAdjusted) && engineReason && (
                  <div style={{ marginTop: 6, fontSize: 12 }}>
                    <Text type="secondary">引擎调整原因：</Text>
                    <Text>{engineReason.detail || engineReason.text}</Text>
                    {engineReason.stage ? <Text type="secondary">（阶段 {engineReason.stage}）</Text> : null}
                  </div>
                )}
                </>
              )}
            </div>
          );
        })}
        {/* 分页器:仅当总数超过一页才显示,避免只有 1-2 条时挤占空间 */}
        {historyTotal > HISTORY_PAGE_SIZE && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
            <Pagination
              size="small"
              current={historyCurrent}
              pageSize={HISTORY_PAGE_SIZE}
              total={historyTotal}
              showSizeChanger={false}
              onChange={(page) => setHistoryCurrent(page)}
            />
          </div>
        )}
      </div>
    );
  }

}

/** BOS 文件列表(内嵌小组件),对齐原 SceneDetail 页的 FileList。 */
function BosFileList({
  files, selectedKey, onSelect, modifiedNames,
}: {
  modifiedNames?: Set<string>;
  files: SceneBosConfigFileItem[];
  selectedKey: string | null;
  onSelect: (row: SceneBosConfigFileItem) => void;
}) {
  const columns: ColumnsType<SceneBosConfigFileItem> = [
    {
      title: '文件名', dataIndex: 'name', ellipsis: true,
      render: (name: string, row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <FileTextOutlined style={{ color: selectedKey === row.key ? ACCENT : '#8c8c8c' }} />
          <Text style={{ color: selectedKey === row.key ? ACCENT : undefined, fontSize: 12 }}>
            {name}{modifiedNames?.has(name) ? ' *' : ''}
          </Text>
        </span>
      ),
    },
    {
      title: '大小', dataIndex: 'size', width: 70,
      render: (s: number) => <span style={{ fontSize: 11 }}>{formatSize(s ?? 0)}</span>,
    },
  ];
  return (
    <Table<SceneBosConfigFileItem>
      size="small"
      rowKey="key"
      columns={columns}
      dataSource={files}
      pagination={false}
      showHeader={false}
      onRow={(row) => ({
        onClick: () => onSelect(row),
        style: { cursor: 'pointer' },
      })}
    />
  );
}
